/**
 * Cash-flow rollup logic for Progear Finance 2.0, module 1.1 (the primary view).
 *
 * This is the ONE definition of how dated money movements roll up into a daily
 * cash position and a forward-projected running balance (replit.md master rule:
 * each calculation lives in finance-core or a Postgres view, never in a
 * component). It is pure and deterministic: integer cents in, integer cents out,
 * no JS float for money (L9), no I/O, no Date.now() — `today` is always passed in
 * so the result is reproducible and golden-testable.
 *
 * Business rules encoded here (see docs/modules/1.1-cash-flow-calendar.md):
 *  - Honest balance (cash basis for the past): a pending entry whose expected
 *    date is already in the past does NOT sit in the past inflating it — it is
 *    CARRIED FORWARD to `today`. Only entries that actually moved (an actual
 *    date) count on their actual date.
 *  - Certainty is explicit: every contribution is bucketed actual / committed /
 *    forecast. An entry that has actually moved is `actual` regardless of the
 *    certainty it was planned with; a still-pending entry keeps its own.
 *  - The running balance is GROSS (a bank balance is always gross) and is
 *    anchored at `today` by `openingBalance` (the LHV real-time balance in
 *    production; a manual anchor until module 1.4). It projects forward from the
 *    anchor and reconstructs backward from it using actual movements.
 */

import type { Money } from './money';
import { splitMoney } from './vat';

/** How certain a contribution is. */
export type Certainty = 'actual' | 'committed' | 'forecast';

/** Direction of a money movement. */
export type CashFlowKind = 'income' | 'cost';

/**
 * The cash-flow line an entry belongs to — the 7 named lines of the primary view
 * (replit.md module 1.3). One truth for the taxonomy; UI labels are Estonian
 * (sale=Müük, direct_cost=Otsekulud, operating_cost=Tegevuskulud,
 * payroll=Tööjõukulud, financial_cost=Finantskulud, tax_agreement=Maksud,
 * loan=Laenud). `tax_agreement` is the EMTA line — the KMD declaration entries
 * plus deferred tax-debt (ajatamine) schedules; the enum KEY predates the
 * label and stays for schema stability. A line is normally one-directional, but `loan` can carry both a
 * drawdown (income) and a repayment (cost).
 */
export type CashFlowCategory =
  | 'sale'
  | 'direct_cost'
  | 'operating_cost'
  | 'payroll'
  | 'financial_cost'
  | 'tax_agreement'
  | 'loan';

/** The 7 lines in canonical display order (income line first, then cost lines). */
export const CASH_FLOW_CATEGORIES = [
  'sale',
  'direct_cost',
  'operating_cost',
  'payroll',
  'financial_cost',
  'tax_agreement',
  'loan',
] as const satisfies readonly CashFlowCategory[];

/** Where the entry came from. `manual` is module 1.1's own planner overlay; the
 *  rest are added as later modules wire their sources into the same model. */
export type CashFlowSource =
  | 'manual'
  | 'smartaccounts'
  | 'erply'
  | 'pipedrive'
  | 'loan'
  | 'tax'
  | 'lhv'
  | 'forecast'
  | 'payment_plan'
  | 'contract';

/**
 * One recorded KMD tax payment — a single installment event (manual or LHV bank
 * import) against the KMD tax obligation. Used within {@link KmdSettlement}
 * attached to positive-payable `tax:kmd` entries. Integer cents (L9); dates ISO
 * `YYYY-MM-DD`. Superseded payments are retained for display but excluded from
 * effective sums (the `supersededByPaymentId` field gates them).
 */
export interface KmdPaymentEvent {
  /** Stable payment id (UUID or external reference). */
  id: string;
  /** ISO date the payment was made, `YYYY-MM-DD`. */
  paidOn: string;
  /** Amount paid, positive integer cents. */
  amountCents: number;
  /** Whether the payment was entered manually or imported from LHV. */
  source: 'manual' | 'lhv';
  /** When set, this payment has been superseded by another and must be
   *  excluded from effective sums (retained for display history only). */
  supersededByPaymentId: string | null;
  /** Optional LHV bank link reference. */
  bankLinkId: string | null;
}

/**
 * KMD settlement metadata attached to a positive-payable `tax:kmd` cash-flow
 * entry. `entry.money` stays the IMMUTABLE forecast baseline (the materialized
 * gross obligation) — the settlement state is carried HERE, never by mutating
 * the entry money. Applied only to entries whose `taxKindOf` is `KMD` and whose
 * net payable is positive (a payment obligation, not a refund).
 *
 * `month`       — the KMD accrual month this settlement covers (`YYYY-MM`),
 *                 mirrors the `tax:kmd:<month>` external id.
 * `finalized`   — when true the declaration is fully closed: the remaining
 *                 forecast is treated as 0 even if actual payments are below
 *                 the forecast. Actual above forecast is never clipped.
 * `payments`    — the chronological list of payment installment events. Multiple
 *                 installments add (never winner-take-all). Superseded events are
 *                 excluded from effective sums but retained for display metadata.
 */
export interface KmdSettlement {
  /** KMD accrual month, `YYYY-MM`. */
  month: string;
  /** True when the declaration has been fully finalized (remaining forecast → 0). */
  finalized: boolean;
  /** Recorded payment installment events (manual + LHV). */
  payments: KmdPaymentEvent[];
}

/** A single dated money movement. Dual-anchored: an expected date and, once it
 *  has happened, an actual date. Dates are ISO calendar days (`YYYY-MM-DD`). */
export interface CashFlowEntry {
  id: string;
  kind: CashFlowKind;
  party: string;
  category: CashFlowCategory;
  certainty: Certainty;
  source: CashFlowSource;
  money: Money;
  /** The day the entry is expected to move. */
  expectedDate: string;
  /** The day it actually moved, or null while still pending. */
  actualDate: string | null;
  /**
   * Optional KMD settlement metadata. Present only on positive-payable
   * `tax:kmd` entries (source `tax`, category `tax_agreement`). When present,
   * placement logic delegates to {@link resolveKmdPlacements} so all
   * aggregators (rollup, line, party, exports) share the same slices.
   * `entry.money` remains the immutable forecast baseline.
   */
  kmdSettlement?: KmdSettlement;
}

// ------------------------------------------------------------------- priority

/**
 * A user-assigned management priority on a cash-flow document — a triage /
 * display property, NOT part of the rollup math, so (like settlement metadata)
 * it is deliberately NOT a field of the math model {@link CashFlowEntry}; the
 * web / API entry carries it structurally. UI labels are Estonian
 * (critical=Kriitiline, important=Oluline, focus=Fookuses); codes stay English
 * (replit.md terminology). A prioritised document is never folded into the
 * calendar's carried-forward summary group — it stays a distinct prominent chip.
 */
export type Priority = 'critical' | 'important' | 'focus';

/** The priorities in descending urgency — the canonical display / facet order. */
export const PRIORITIES = ['critical', 'important', 'focus'] as const satisfies readonly Priority[];

/** Sort rank (0 = most urgent); unprioritised documents sort after all of these. */
export const PRIORITY_ORDER: Record<Priority, number> = {
  critical: 0,
  important: 1,
  focus: 2,
};

// ----------------------------------------------------------------- settlement

/**
 * The settlement state a document shows in BOTH lenses of the primary view
 * (L14). It is a property of the document as the app sees it (how much of its
 * gross has been paid), distinct from the rollup math (which only cares about
 * money + dates). UI maps these to Estonian (settled=Tasutud, overdue=Üle
 * tähtaja, partial=Osaliselt, planned=Plaan, pending=Ootel).
 */
export type SettlementStatus = 'settled' | 'overdue' | 'partial' | 'pending' | 'planned';

/**
 * The minimal shape needed to compute settlement — a structural interface, NOT
 * a change to {@link CashFlowEntry}, so the math model stays free of display-only
 * settlement metadata (doc numbers, payment counts). Any entry that carries
 * these fields (the API / web entry does) satisfies it. `paidCents` is the
 * event-sourced sum of payments (pgfin `cash_flow_payment`); `actualDate` is the
 * legacy binary mark-paid. When the per-source breakdown (`sources`, from
 * {@link SourceAwareSettleable}) is present and non-empty it is the truth — the
 * cross-source `paidCents` sum (which double-counts competing observations) and
 * the legacy `actualDate` shortcut are NOT used for the paid amount then.
 */
export interface SettleableEntry extends SourceAwareSettleable, PrepaymentOffsettable {
  money: Money;
  expectedDate: string;
  certainty: Certainty;
  actualDate: string | null;
  /** Sum of recorded payment events against this entry, integer cents. */
  paidCents: number;
  /**
   * Sum of settlement adjustments against this entry (rounding / bad-debt
   * write-off, factoring), integer cents (pgfin `cash_flow_settlement_adjustment`).
   * Counts toward "settled" but NOT toward cash "paid" — kept separate so the
   * register can show real cash vs a write-off.
   */
  adjustedCents: number;
}

// -------------------------------------------------- multi-source settlement

/**
 * One system's OBSERVATION of how an entry settled (pgfin
 * `cash_flow_settlement_by_source`): the cash that source reports, the newest
 * date it reported, when it last touched the entry, and how many events it
 * contributed. Several systems can observe the SAME settlement at once (the bank,
 * the accounting system, the sales system, a human) — these are competing
 * observations of the same euro, NOT additive cash. Integer cents (L9).
 */
export interface SourceSettlement {
  source: CashFlowSource;
  /** Cash THIS source reports settled (sum of its own events), integer cents. */
  paidCents: number;
  /** Newest settlement date this source reported, ISO `YYYY-MM-DD`, or null. */
  latestPaidOn: string | null;
  /** When this source last touched the entry (ISO timestamp), or null. */
  observedAt: string | null;
  /** How many payment events this source contributed. */
  paymentCount: number;
}

/**
 * The minimal shape needed to resolve source-aware settlement: the per-source
 * breakdown. A structural interface (the API / web entry carries it), so the math
 * model {@link CashFlowEntry} stays free of settlement metadata. Absent/undefined
 * `sources` = no per-source data (the legacy path applies); an empty array = there
 * IS per-source data and it is none.
 */
export interface SourceAwareSettleable {
  sources?: SourceSettlement[];
}

/**
 * Settlement-source priority — which system's observation is the TRUTH when
 * several report the same settlement: the bank (LHV) over the accounting system
 * (SmartAccounts) over the sales system (Erply) over a human (Manuaal). The ONE
 * place this ranking lives (master rule); lower index = higher priority.
 */
export const SETTLEMENT_SOURCE_PRIORITY = [
  'lhv',
  'smartaccounts',
  'erply',
  'manual',
] as const satisfies readonly CashFlowSource[];

/**
 * Priority rank of a settlement source (0 = highest). A source not in
 * {@link SETTLEMENT_SOURCE_PRIORITY} (e.g. a non-cash entry source) ranks AFTER
 * every ranked one, so it can never win the truth over a real cash source.
 */
function settlementPriorityRank(source: CashFlowSource): number {
  const i = (SETTLEMENT_SOURCE_PRIORITY as readonly CashFlowSource[]).indexOf(source);
  return i === -1 ? SETTLEMENT_SOURCE_PRIORITY.length : i;
}

/**
 * The per-source settlement breakdown ordered by TRUTH priority (LHV → SA →
 * Erply → Manuaal → any other) — the ONE place the sources are ranked, so the
 * detail's per-source overview shows them in a single canonical order (L14). Pure;
 * the input is never mutated. An entry with no `sources` yields an empty list.
 */
export function settlementSourcesOf(e: SourceAwareSettleable): SourceSettlement[] {
  return [...(e.sources ?? [])].sort(
    (a, b) => settlementPriorityRank(a.source) - settlementPriorityRank(b.source),
  );
}

/**
 * The single source whose observation is the settlement TRUTH: the
 * highest-priority source that actually reports cash (`paidCents !== 0`). Sources
 * with no cash are skipped (a zero observation is not a settlement). Returns null
 * when no source reports cash. The ONE place the winning source is chosen — never
 * a sum across sources, which would double-count the same euro (master rule). Pure.
 */
export function effectivePaymentSourceOf(e: SourceAwareSettleable): SourceSettlement | null {
  let best: SourceSettlement | null = null;
  let bestRank = Number.POSITIVE_INFINITY;
  for (const s of e.sources ?? []) {
    if (s.paidCents === 0) continue;
    const rank = settlementPriorityRank(s.source);
    if (rank < bestRank) {
      best = s;
      bestRank = rank;
    }
  }
  return best;
}

/**
 * The cash paid on an entry per the source-aware TRUTH, integer cents: the
 * winning source's own cash (NOT a cross-source sum). 0 when no source reports
 * cash. The ONE place source-resolved "paid" is defined (master rule; L9 — integer
 * cents only); {@link paidAmountOf} uses it when per-source data is present. Pure.
 */
export function effectivePaidAmountOf(e: SourceAwareSettleable): number {
  const winner = effectivePaymentSourceOf(e);
  return winner ? winner.paidCents : 0;
}

/**
 * The priority rank of the entry's settlement TRUTH source (0 = highest, in
 * {@link SETTLEMENT_SOURCE_PRIORITY} order), or `Number.MAX_SAFE_INTEGER` when no
 * source reports cash — so a register sort by settlement source orders LHV →
 * SmartAccounts → Erply → Manuaal and pushes the unsettled rows last. The ONE
 * place the winning source's rank is exposed (master rule — the ranking lives
 * here, never re-derived in a component / lens). Pure.
 */
export function effectivePaymentSourceRankOf(e: SourceAwareSettleable): number {
  const winner = effectivePaymentSourceOf(e);
  return winner ? settlementPriorityRank(winner.source) : Number.MAX_SAFE_INTEGER;
}

// ------------------------------------------------ prepayment offset (ETT↔ARV)

/**
 * One document reached from THIS entry through a prepayment-offset link
 * (pgfin `cash_flow_entry_related_document`). The DB exposes the link FACTS only;
 * finance-core RESOLVES the money from them — the database never nets (L9). This is
 * the minimal structural shape the offset math needs (the API / web entry carries
 * the richer display identity on top), so the math model {@link CashFlowEntry} stays
 * free of linkage metadata. `direction` is from this entry's point of view:
 *  - `applied_from` — a received prepayment (ettemaksuarve) that FUNDS this final
 *    invoice (this entry is the lõpparve; these reduce this entry's money via
 *    {@link prepaymentOffsetAmountOf}).
 *  - `applies_to`   — the final invoice this prepayment funds (this entry is the
 *    ettemaksuarve). When the FINAL was cash-paid directly, this prepayment's open
 *    expectation is covered via {@link prepaymentCoveredByFinalAmountOf} — a
 *    prepayment whose final is already paid will never be collected itself.
 */
export type CashFlowRelationKind = 'prepayment_offset' | 'credit_note';

export interface RelatedCashFlowDocument {
  /**
   * Which linkage this row is (`cash_flow_entry_link.kind`). `prepayment_offset`
   * nets money through {@link prepaymentOffsetAmountOf} /
   * {@link prepaymentCoveredByFinalAmountOf}; `credit_note` nets through its OWN
   * pair ({@link creditNoteOffsetAmountOf} / {@link creditNoteAppliedAmountOf}) —
   * the two kinds NEVER read each other's links (mixing them would double-count).
   * Every consumer keys behaviour off this, never off the doc types.
   */
  relationKind: CashFlowRelationKind;
  /** Whether the related doc funds this one (`applied_from`) or is funded by it. */
  direction: 'applied_from' | 'applies_to';
  /** The link capacity, integer cents (`cash_flow_entry_link.amount_cents`). */
  amountCents: number;
  /** How much of the RELATED document is itself settled in cash, integer cents —
   *  the GATE: a prepayment offsets the final only up to its OWN received cash, so
   *  an unpaid ettemaksuarve never settles the lõpparve. */
  relatedSettledCents: number;
  /** The link row id — an OPTIONAL stable tiebreaker for sequential depletion
   *  ({@link creditNoteOffsetAmountOf}); absent on legacy shapes (order falls back
   *  to the list order, still deterministic for a single link — the common case). */
  linkId?: string;
  /** When the link takes effect (ISO date) — optional, the primary depletion sort key. */
  effectiveOn?: string;
  /** The RELATED document's own gross, integer cents — needed by the credit-note
   *  gate ({@link creditNoteAppliedAmountOf}) to size the credited invoice's
   *  uncollected remainder. Absent = 0 (legacy shape → the gate yields 0, safe). */
  relatedGrossCents?: number;
  /** Sum of `prepayment_offset` link capacities where the RELATED document is the
   *  final invoice, integer cents (2-hop fact from the 0040 view). Absent = 0. */
  relatedPrepaymentLinkCents?: number;
  /** Those linked prepayments' OWN settled cash (paid + adjusted), integer cents
   *  (2-hop fact from the 0040 view). Absent = 0. */
  relatedPrepaymentPaidCents?: number;
}

/**
 * The minimal shape needed to resolve the prepayment offset: the related-document
 * links. A structural interface (the API / web entry carries it), so the math model
 * {@link CashFlowEntry} stays free of linkage metadata. Absent/undefined = no links
 * (the common case — the offset is 0 and every existing caller is unaffected).
 */
export interface PrepaymentOffsettable {
  relatedDocuments?: RelatedCashFlowDocument[];
}

/**
 * How much of THIS entry's gross is offset by RECEIVED prepayments, integer cents:
 * the sum over the `applied_from` links (prepayments funding this final invoice) of
 * min(link capacity, the prepayment's own settled cash), clamped ≥ 0 — an unpaid
 * ettemaksuarve contributes nothing.
 *
 * When a SYNCED source reports cash on THIS entry the offset is instead the ECHO
 * amount: a synced system (Erply) books the prepayment application as a payment
 * row on the final itself (the real invoice 1257043 case — ETT cash + an echoed
 * payment are the SAME euros). That echoed "payment" is NOT new cash — the money
 * arrived when the ettemaksuarve was paid — so it is reclassified here as the
 * applied prepayment: offset = min(link capacity usable, the winner's reported
 * cash), and the cash accessors subtract the same amount via
 * {@link prepaymentEchoCashOf} (one euro, one classification — L9). Counting the
 * raw link capacity on top of the echoed cash would double-count (a vendor-UNPAID
 * remainder read "settled"); counting the echoed cash as laekumine would inflate
 * received cash (the ETT already counted it). Manual cash is NEVER an echo (a
 * human books real partial cash beside a funding ETT — both count). The ONE place
 * the prepayment offset is resolved (master rule; L9 — integer cents only): the DB
 * exposes the link facts, finance-core nets. An entry with no links yields 0, so the
 * projection is unchanged for every non-linked document. Pure and deterministic.
 */
export function prepaymentOffsetAmountOf(e: PrepaymentOffsettable & SourceAwareSettleable): number {
  let usable = 0;
  for (const r of e.relatedDocuments ?? []) {
    // credit_note links net through their OWN accessors (creditNoteOffsetAmountOf /
    // creditNoteAppliedAmountOf) — reading them here too would double-count (L9).
    if (r.relationKind !== 'prepayment_offset') continue;
    if (r.direction !== 'applied_from') continue;
    const u = Math.min(r.amountCents, r.relatedSettledCents);
    if (u > 0) usable += u;
  }
  if (usable <= 0) return 0;
  const winner = effectivePaymentSourceOf(e);
  if (winner !== null && winner.source !== 'manual') {
    // Synced winner → its payments EMBED the application; the offset is exactly
    // the echoed share of that cash (never more than the vendor actually applied).
    return Math.min(usable, Math.max(0, winner.paidCents));
  }
  return usable;
}

/**
 * The share of THIS entry's winning-source cash that is a synced ECHO of applied
 * prepayments, integer cents — exactly the amount
 * {@link prepaymentOffsetAmountOf} classifies as offset on a synced winner. Every
 * cash accessor ({@link paidAmountOf}, placement cash) subtracts this, so the
 * echoed euro is counted ONCE (as the applied prepayment), never also as new
 * laekumine — the real money already counted when the ettemaksuarve was paid.
 * 0 for a manual winner, an unsettled entry, or an entry with no usable
 * `applied_from` links. Pure and deterministic (L9 — integer cents only).
 */
export function prepaymentEchoCashOf(e: PrepaymentOffsettable & SourceAwareSettleable): number {
  const winner = effectivePaymentSourceOf(e);
  if (winner === null || winner.source === 'manual') return 0;
  return prepaymentOffsetAmountOf(e);
}

/**
 * The minimal shape needed to resolve the REVERSE prepayment coverage: the links
 * plus the entry's OWN money and settlement facts (the cap below needs them). All
 * settlement members are optional, so a {@link SettleableEntry} and a
 * {@link PlaceableEntry} both satisfy it structurally.
 */
export type PrepaymentCoverable = PrepaymentOffsettable &
  SourceAwareSettleable & {
    money: Money;
    actualDate?: string | null;
    paidCents?: number;
    adjustedCents?: number;
  };

/**
 * How much of THIS prepayment (ettemaksuarve) is COVERED by direct cash settlement
 * of the final invoice it applies to, integer cents — the REVERSE gate of
 * {@link prepaymentOffsetAmountOf}. Real-world case (Erply): the customer's payment
 * is recorded on the LÕPPARVE, not the ettemaksuarve — the ETT then stays "unpaid"
 * forever, and without this gate it haunts the projection as a phantom overdue
 * expectation (nobody pays a prepayment whose final invoice is already cash-paid).
 * Sum over the `applies_to` prepayment-offset links of min(link capacity, the
 * FINAL's own cash+adjustment settlement — offsets are computed, never stored, so
 * this can't echo back), then capped at the prepayment's OWN uncollected remainder
 * (gross − its own cash − adjustments, clamped ≥ 0) so a cash-collected prepayment
 * is never re-covered — when BOTH legs are paid, the forward gate already nets the
 * final and this one yields 0 (no double suppression; the over-collection stays
 * visible). Gated strictly on `prepayment_offset`: a credit note also carries an
 * `applies_to` link (display-only) and the final's payment must NEVER swallow the
 * refund expectation. The ONE place this coverage is resolved (master rule; L9 —
 * integer cents only). Pure and deterministic.
 */
export function prepaymentCoveredByFinalAmountOf(e: PrepaymentCoverable): number {
  let linked = 0;
  for (const r of e.relatedDocuments ?? []) {
    if (r.relationKind !== 'prepayment_offset') continue;
    if (r.direction !== 'applies_to') continue;
    const usable = Math.min(r.amountCents, r.relatedSettledCents);
    if (usable > 0) linked += usable;
  }
  if (linked <= 0) return 0;
  const own = ownCashOf(e) + (e.adjustedCents ?? 0);
  return Math.min(linked, Math.max(0, e.money.gross - own));
}

/**
 * The TOTAL prepayment offset of an entry, integer cents: the forward gate (a paid
 * ettemaksuarve funding this final — {@link prepaymentOffsetAmountOf}) plus the
 * reverse gate (this prepayment covered by its cash-paid final —
 * {@link prepaymentCoveredByFinalAmountOf}). The ONE accessor every settlement /
 * projection consumer nets through, so an ETT↔ARV pair reads consistently from
 * either side (master rule; L9 — integer cents only). Pure.
 */
export function totalPrepaymentOffsetOf(e: PrepaymentCoverable): number {
  return prepaymentOffsetAmountOf(e) + prepaymentCoveredByFinalAmountOf(e);
}

// ------------------------------------------------ credit-note netting (KRE↔ARV)

/** The entry's OWN cash, resolved the same way as {@link paidAmountOf} (source-
 *  priority truth when the per-source breakdown is present; else legacy mark-paid /
 *  events) — shared by the reverse-prepayment and credit-note gates. */
function ownCashOf(e: PrepaymentCoverable): number {
  return e.sources && e.sources.length > 0
    ? effectivePaidAmountOf(e) - prepaymentEchoCashOf(e)
    : e.actualDate
      ? e.money.gross
      : e.paidCents ?? 0;
}

/**
 * How much of THIS invoice's gross is settled by APPLIED CREDIT NOTES, integer
 * cents — the invoice side of credit-note netting. A kreeditarve issued against an
 * invoice cancels the uncollected part of that claim: nobody pays the credited
 * share, so leaving it in the projection keeps a phantom overdue expectation alive
 * (and its mirror image on the credit note — a ±X junk pair in the overdue bucket).
 * Sum over the `applied_from` credit-note links (the credit
 * notes crediting this invoice), each depleting min(link capacity, the REMAINING
 * uncollected claim), where the starting claim is gross − own cash − adjustments −
 * {@link totalPrepaymentOffsetOf} clamped ≥ 0 — cash already received (or a paid
 * prepayment funding it) is NEVER re-absorbed, so a cash-paid invoice yields 0 and
 * its credit note stays a visible refund expectation. Depletion is SEQUENTIAL in a
 * STABLE order (effectiveOn, then linkId) so both lenses resolve identically (L14).
 * Non-negative (an invoice is stored positive; the signed mirror lives on the
 * credit-note side — {@link creditNoteAppliedAmountOf}). The ONE place the invoice
 * side is resolved (master rule; L9 — integer cents only). Pure and deterministic.
 */
export function creditNoteOffsetAmountOf(e: PrepaymentCoverable): number {
  const links = (e.relatedDocuments ?? []).filter(
    (r) => r.relationKind === 'credit_note' && r.direction === 'applied_from',
  );
  if (links.length === 0) return 0;
  const own = ownCashOf(e) + (e.adjustedCents ?? 0);
  let remaining = Math.max(0, e.money.gross - own - totalPrepaymentOffsetOf(e));
  const ordered = [...links].sort(
    (a, b) =>
      (a.effectiveOn ?? '').localeCompare(b.effectiveOn ?? '') ||
      (a.linkId ?? '').localeCompare(b.linkId ?? ''),
  );
  let offset = 0;
  for (const r of ordered) {
    const usable = Math.min(r.amountCents, remaining);
    if (usable > 0) {
      offset += usable;
      remaining -= usable;
    }
  }
  return offset;
}

/**
 * How much of THIS credit note (kreeditarve) is APPLIED against the invoice it
 * credits, integer cents, SIGNED in the document's own direction (a credit note is
 * stored NEGATIVE, so an applied credit note yields a negative offset — the mirror
 * of {@link creditNoteOffsetAmountOf} on the invoice). Per `applies_to` credit-note
 * link, the absorbable claim is the credited invoice's UNCOLLECTED remainder ONLY:
 *
 *   * `invoiceUncollected` = max(0, related gross − related cash/adjustments −
 *     min(prepay link caps, prepay paid)) — what the invoice itself still awaits
 *     (its paid-prepayment funding subtracted, mirroring the forward gate).
 *
 * CASH IS THE GATE (business rule, 2026-07-21): if the credited invoice carries
 * ANY settled cash — a bank receipt OR an Erply TRANSFER that carried a physical
 * prepayment receipt onto the final — that money was REALLY collected somewhere on
 * the chain, so the credit note is a GENUINE refund obligation and stays OPEN until
 * a real repayment (LHV) lands on it. Tasaarveldus (netting with no cash moving) is
 * legitimate ONLY when the credited claim was never collected: unpaid invoice +
 * unpaid credit note cancel each other. (The former "triangle netting" arm — which
 * read a TRANSFER-settled invoice as cashless and auto-applied its credit note —
 * was removed for exactly this reason: it hid real refund obligations.)
 *
 * Each link contributes min(capacity, absorbable); the sum is then capped at the
 * credit note's OWN unrefunded remainder (|gross| − refund cash − adjustments,
 * clamped ≥ 0) so a cash-refunded credit note is never re-absorbed. A credit note
 * against a fully cash-paid invoice therefore yields 0 — a visible refund
 * expectation. KNOWN PROPERTY (documented, accepted): with several prepayment
 * links the aggregate min can overstate the funding subtraction vs the per-link
 * forward sum (no such data exists; the DB exposes the aggregate facts only). The
 * ONE place the credit-note side is resolved (master rule; L9). Pure and
 * deterministic.
 */
export function creditNoteAppliedAmountOf(e: PrepaymentCoverable): number {
  const dir = settlementDirectionOf(e.money.gross);
  if (dir > 0) return 0; // only a negative-gross document is an applicable credit note
  let applied = 0;
  for (const r of e.relatedDocuments ?? []) {
    if (r.relationKind !== 'credit_note') continue;
    if (r.direction !== 'applies_to') continue;
    const relGross = r.relatedGrossCents ?? 0;
    const prepayCaps = r.relatedPrepaymentLinkCents ?? 0;
    const prepayPaid = r.relatedPrepaymentPaidCents ?? 0;
    const invoiceUncollected = Math.max(
      0,
      relGross - r.relatedSettledCents - Math.min(prepayCaps, prepayPaid),
    );
    const usable = Math.min(r.amountCents, invoiceUncollected);
    if (usable > 0) applied += usable;
  }
  if (applied <= 0) return 0;
  const own = ownCashOf(e) + (e.adjustedCents ?? 0);
  const unrefunded = Math.max(0, dir * (e.money.gross - own));
  const magnitude = Math.min(applied, unrefunded);
  if (magnitude === 0) return 0; // avoid the IEEE −0 (dir is −1 here)
  return dir * magnitude;
}

/**
 * The TOTAL document offset of an entry, integer cents, SIGNED in the document's
 * own direction: the prepayment pair ({@link totalPrepaymentOffsetOf}, ≥ 0 — those
 * documents are stored positive) plus the credit-note pair
 * ({@link creditNoteOffsetAmountOf} ≥ 0 on the invoice,
 * {@link creditNoteAppliedAmountOf} ≤ 0 on the credit note). The ONE accessor every
 * settlement / projection consumer nets through — settled, effective money,
 * placement and status all read THIS, so a linked pair reads consistently from
 * either side and from either lens (master rule; L14; L9 — integer cents only).
 * The label-level split stays available through the underlying accessors. Pure.
 */
export function totalDocumentOffsetOf(e: PrepaymentCoverable): number {
  return totalPrepaymentOffsetOf(e) + creditNoteOffsetAmountOf(e) + creditNoteAppliedAmountOf(e);
}

/**
 * The entry's money as it must hit the PROJECTION (calendar grid, treasury matrix,
 * party rollups), with received prepayments netted out: a final invoice funded by a
 * paid prepayment contributes only its UNFUNDED remainder, so the same euro is never
 * counted twice — once as the prepayment's cash-in, once as the final invoice — and
 * an APPLIED credit note (and the credited share of its invoice) contributes nothing:
 * the claim was cancelled, not collected. With no offset (the common case) the
 * original money is returned unchanged. Otherwise the offset — SIGNED in the
 * document's own direction ({@link totalDocumentOffsetOf}) — is netted out through
 * finance-core's own {@link splitMoney}, whose orientation-safe clamp reduces a
 * credit note's negative gross toward 0 exactly like an invoice's positive gross
 * (VAT re-derived from the same rate), so net/vat/gross stay consistent. Both the projection AND the
 * register's aggregates net through this — the register totals/rows use the signed &
 * settlement `*Effective*` twins below, so an ETT↔ARV pair is never summed twice;
 * only a single document's OWN hover/detail still shows its face value. The ONE place
 * the netted amount is defined (master rule; L9). Pure.
 */
export function effectiveCashFlowMoneyOf(e: CashFlowEntry & PrepaymentCoverable): Money {
  const offset = totalDocumentOffsetOf(e);
  if (offset === 0) return e.money;
  return splitMoney(e.money, offset).remainder;
}

/**
 * How much of the entry is settled in CASH, integer cents. When the per-source
 * breakdown is present (≥1 source) it is the truth — the highest-priority source
 * with cash wins outright via {@link effectivePaidAmountOf}, never a sum across
 * sources (no double-count), and the legacy `actualDate` is display-only. With no
 * per-source data the legacy path applies: a binary mark-paid (`actualDate` set,
 * no events) reads as fully paid — reconciling the old model with event-based
 * partial settlement — otherwise the event-sourced `paidCents` is the truth (the
 * view's own `outstanding_cents` is actualDate-blind, so it is never trusted
 * here). Subtracts the synced prepayment-application ECHO
 * ({@link prepaymentEchoCashOf}) — that share of the winner's "cash" is the
 * applied ettemaksuarve, not new money, and it counts as offset instead (one
 * euro, one classification). The ONE place "paid" is defined (master rule; L9 —
 * integer cents only).
 */
export function paidAmountOf(e: SettleableEntry): number {
  if (e.sources && e.sources.length > 0) return effectivePaidAmountOf(e) - prepaymentEchoCashOf(e);
  return e.actualDate ? e.money.gross : e.paidCents;
}

/**
 * How much of the entry was closed by a settlement adjustment (rounding /
 * bad-debt write-off, factoring), integer cents — NOT cash. Kept distinct from
 * {@link paidAmountOf} so the register shows real cash vs a write-off (L9).
 */
export function adjustedAmountOf(e: SettleableEntry): number {
  return e.adjustedCents;
}

/**
 * Total settled, integer cents = cash paid + adjustments + the SIGNED document
 * offset (received prepayments + applied credit notes —
 * {@link totalDocumentOffsetOf}). The ONE definition of "settled" —
 * outstanding/status derive from it, so a written-off receivable, a final invoice
 * covered by a paid prepayment, a credited invoice OR an applied kreeditarve reads
 * as closed in every lens (master rule; L9 — integer cents only).
 */
export function settledAmountOf(e: SettleableEntry): number {
  return paidAmountOf(e) + adjustedAmountOf(e) + totalDocumentOffsetOf(e);
}

/**
 * Outstanding (unpaid) amount, integer cents = gross − settled. Not clamped:
 * overpayment / over-adjustment yields a negative outstanding, mirroring the DB
 * (no settled ≤ gross constraint). No component subtracts money itself — it
 * calls this.
 */
export function outstandingOf(e: SettleableEntry): number {
  return e.money.gross - settledAmountOf(e);
}

/**
 * The document's settlement DIRECTION: +1 for a document stored positive (invoice,
 * bill), −1 for one stored negative (a credit note IS stored negative — its refund
 * arrives as negative cash). Every "is it covered?" comparison multiplies both
 * sides by this, so covered means the same thing on either side of zero: an unpaid
 * credit note (settled 0, gross −4 240 000) is NOT settled, and a fully-refunded
 * one (settled −4 240 000) IS. Zero gross keeps +1 — 0 ≥ 0 still reads settled,
 * unchanged. The ONE definition of the direction (master rule; L9) — sign logic
 * never leaks into a component or a bare comparison elsewhere.
 */
function settlementDirectionOf(grossCents: number): 1 | -1 {
  return grossCents < 0 ? -1 : 1;
}

/**
 * Paid share as a whole percent, for the progress cue both lenses show. A
 * zero-gross entry is 0% (no division by zero). A ratio, so the only rounded
 * settlement value (never money — L9).
 */
export function paidPctOf(e: SettleableEntry): number {
  if (e.money.gross === 0) return 0;
  return Math.round((paidAmountOf(e) / e.money.gross) * 100);
}

/**
 * Settled share as a whole percent (cash + adjustments) — the resolved-progress
 * cue both lenses show, so a written-off receivable reads as resolved everywhere
 * (L14). A zero-gross entry is 0% (no division by zero). A ratio, never money (L9).
 */
export function settledPctOf(e: SettleableEntry): number {
  if (e.money.gross === 0) return 0;
  return Math.round((settledAmountOf(e) / e.money.gross) * 100);
}

/**
 * The canonical settlement status. Precedence (approved design): settled
 * (settled covers gross — cash OR a write-off closes it) → overdue (still owed
 * and past due) → partial (some settled) → planned (a forecast) / pending. The
 * `outstanding > 0` of the source design is implied once `settled` is excluded,
 * so it is omitted here. Comparisons are made in the document's own direction
 * ({@link settlementDirectionOf}) so an UNPAID credit note (0 settled vs a
 * negative gross) reads overdue/pending — never "settled" — and a refunded one
 * closes. `today` is passed in (pure, golden-testable).
 */
export function settlementStatusOf(e: SettleableEntry, today: string): SettlementStatus {
  const dir = settlementDirectionOf(e.money.gross);
  if (dir * settledAmountOf(e) >= dir * e.money.gross) return 'settled';
  if (e.expectedDate < today) return 'overdue';
  if (dir * settledAmountOf(e) > 0) return 'partial';
  return e.certainty === 'forecast' ? 'planned' : 'pending';
}

/**
 * Settled / outstanding totals across many entries, integer cents. `paid` is the
 * real cash, `adjusted` the write-offs, `prepayment` the received-prepayment offset,
 * `credited` the SIGNED credit-note netting, `settled` their sum; they reconcile to
 * gross with `outstanding` (paid + adjusted + prepayment + credited + outstanding =
 * summed gross).
 */
export interface SettlementTotals {
  /** Real cash settled, integer cents. */
  paid: number;
  /** Settled by adjustment (write-off / factoring), integer cents. */
  adjusted: number;
  /** Settled by a received-prepayment offset (ettemaksuarve → lõpparve), integer cents. */
  prepayment: number;
  /** Settled by credit-note netting (kreeditarve ↔ arve), SIGNED integer cents —
   *  positive on the credited invoice, negative on the applied credit note, so a
   *  netted pair sums to 0 here exactly as it does in the projection. */
  credited: number;
  /** paid + adjusted + prepayment + credited, integer cents. */
  settled: number;
  /** gross − settled, integer cents. */
  outstanding: number;
}

/**
 * Aggregate settlement across a list of entries — the ONE place "total paid /
 * adjusted / prepayment / outstanding" is summed, so neither lens adds them itself
 * (master rule; L9 — integer cents only). Cash goes to `paid`, write-offs to
 * `adjusted`, received-prepayment offsets to `prepayment`, SIGNED credit-note
 * netting to `credited`, the rest to `outstanding`; paid + adjusted + prepayment +
 * credited + outstanding reconciles to the summed gross.
 */
export function sumSettlement(entries: SettleableEntry[]): SettlementTotals {
  let paid = 0;
  let adjusted = 0;
  let prepayment = 0;
  let credited = 0;
  let outstanding = 0;
  for (const e of entries) {
    paid += paidAmountOf(e);
    adjusted += adjustedAmountOf(e);
    prepayment += totalPrepaymentOffsetOf(e);
    credited += creditNoteOffsetAmountOf(e) + creditNoteAppliedAmountOf(e);
    outstanding += outstandingOf(e);
  }
  return {
    paid,
    adjusted,
    prepayment,
    credited,
    settled: paid + adjusted + prepayment + credited,
    outstanding,
  };
}

/**
 * Settlement totals plus the settled SHARE in basis points (0–10000), for the
 * register footer's "tasutud %" cue (item 4). `total` = paid + adjusted +
 * prepayment + credited + outstanding, which reconciles to the summed gross (see
 * {@link sumSettlement}). The share counts cash AND write-offs AND received
 * prepayments AND credit-note netting (settled / total) — a resolved receivable
 * reads as resolved everywhere (L14). It
 * is the ONLY rounded settlement ratio (a ratio, never money — L9); overpayment can
 * push it above 10000 (mirrors reality), and a zero total is 0 (no division by zero).
 */
export interface SettlementRatio extends SettlementTotals {
  /** paid + adjusted + prepayment + credited + outstanding, integer cents (= summed gross). */
  total: number;
  /** Settled share in basis points: round(settled / total · 10000); 0 when total is 0. */
  ratioBps: number;
}

/**
 * Aggregate settlement across entries AND the settled share — the ONE place the
 * footer's settled-percentage is derived, so no lens computes the ratio itself
 * (master rule). Integer cents in; an integer-basis-points ratio out.
 */
export function settlementRatioSummary(entries: SettleableEntry[]): SettlementRatio {
  const { paid, adjusted, prepayment, credited, settled, outstanding } = sumSettlement(entries);
  const total = paid + adjusted + prepayment + credited + outstanding;
  const ratioBps = total === 0 ? 0 : Math.round((settled / total) * 10_000);
  return { paid, adjusted, prepayment, credited, settled, outstanding, total, ratioBps };
}

/**
 * Settlement totals with received prepayments NETTED OUT of BOTH the denominator and
 * the settled figure — the offset-aware twin of {@link sumSettlement}, so the
 * register footer's paid / outstanding stats stay on the SAME basis as its
 * prepayment-netted "Kokku" ({@link signedEffectiveSumEntries}). For each entry the
 * effective gross is `gross − prepaymentOffsetAmountOf` clamped ≥ 0 (the funded part
 * is already the prepayment's OWN cash — counting it here too would double-count);
 * real cash ({@link paidAmountOf}) then adjustments ({@link adjustedAmountOf}) settle
 * that effective gross, each capped so they never exceed it, and the rest is
 * outstanding. A fully-funded lõpparve contributes nothing (effective gross 0), and
 * so does the CREDITED share of an invoice (the claim was cancelled — the offset here
 * is the full SIGNED {@link totalDocumentOffsetOf}); `prepayment` and `credited` are
 * always 0 (netted out, not counted as settled). A NEGATIVE-gross
 * document (credit note) also contributes nothing: its effective gross clamps to 0
 * and its refund cash (negative paid) clamps to ≥ 0 — without that clamp a refunded
 * credit note would inject phantom outstanding (0 − negativePaid > 0) into the
 * footer. Its signed contribution lives in the signed sums, not these stats. The ONE
 * definition of prepayment-netted settlement (master rule; L9 — integer cents). Pure.
 */
export function sumEffectiveSettlement(entries: SettleableEntry[]): SettlementTotals {
  let paid = 0;
  let adjusted = 0;
  let outstanding = 0;
  for (const e of entries) {
    const effGross = Math.max(0, e.money.gross - totalDocumentOffsetOf(e));
    const p = Math.max(0, Math.min(paidAmountOf(e), effGross));
    const a = Math.max(0, Math.min(adjustedAmountOf(e), effGross - p));
    paid += p;
    adjusted += a;
    outstanding += effGross - p - a;
  }
  return { paid, adjusted, prepayment: 0, credited: 0, settled: paid + adjusted, outstanding };
}

/**
 * Prepayment-netted settlement totals PLUS the settled share in basis points — the
 * offset-aware twin of {@link settlementRatioSummary}, reading {@link
 * sumEffectiveSettlement} so the footer's "tasutud %" is measured against the SAME
 * netted total the register displays (no ETT↔ARV double-count in the denominator). A
 * zero total is 0 (no division by zero). The ONE prepayment-netted settled ratio (a
 * ratio, never money — L9). Pure and deterministic.
 */
export function effectiveSettlementRatioSummary(entries: SettleableEntry[]): SettlementRatio {
  const { paid, adjusted, prepayment, credited, settled, outstanding } =
    sumEffectiveSettlement(entries);
  const total = paid + adjusted + prepayment + credited + outstanding;
  const ratioBps = total === 0 ? 0 : Math.round((settled / total) * 10_000);
  return { paid, adjusted, prepayment, credited, settled, outstanding, total, ratioBps };
}

/** A summed monetary amount, all parts in integer cents. */
export interface MoneySum {
  net: number;
  vat: number;
  gross: number;
}

/** Per-certainty money buckets plus their total. */
export interface CertaintyBuckets {
  actual: MoneySum;
  committed: MoneySum;
  forecast: MoneySum;
  total: MoneySum;
}

/** One day of the cash-flow calendar after rollup. */
export interface DayRollup {
  date: string;
  income: CertaintyBuckets;
  cost: CertaintyBuckets;
  /** income.total − cost.total, per field. */
  net: MoneySum;
  /** Overdue income carried into this day (only ever non-zero on `today`). */
  carriedForwardIncome: MoneySum;
  /** Overdue cost carried into this day (only ever non-zero on `today`). */
  carriedForwardCost: MoneySum;
  /** Projected (future) / reconstructed (past) gross balance at end of day. */
  runningBalance: number;
  /**
   * The CONFIRMED-ONLY twin of `runningBalance`: the same anchor-pinned gross
   * balance accumulated over each day's net WITHOUT its forecast buckets
   * (actual + committed money only) — "kus raha päriselt seisab, kui ükski
   * plaan ei täitu". Computed in the SAME pass from the same buckets (one truth
   * per number — never a second client-side rollup), so it equals
   * `runningBalance` exactly on any day whose window holds no forecast money.
   * Drives the primary view's "kinnitatud" balance sub-line in BOTH lenses (L14).
   */
  confirmedRunningBalance: number;
}

/** An entry resolved to where it actually lands on the calendar. */
export interface ResolvedEntry extends CashFlowEntry {
  /** The day the entry contributes to (actual date, or today if carried). */
  effectiveDate: string;
  /** The certainty it contributes as (actual once it has moved). */
  effectiveCertainty: Certainty;
  /** True when an overdue pending entry was rolled forward to today. */
  carriedForward: boolean;
}

export interface RollupOptions {
  /** Today, ISO `YYYY-MM-DD`, in the team time zone (Europe/Tallinn). */
  today: string;
  /** Gross balance at the start of `anchorDate` (the bank anchor), integer cents. */
  openingBalance: number;
  /**
   * The day the `openingBalance` observation was made (the anchor's `as_of_date`),
   * ISO `YYYY-MM-DD`; defaults to `today`. The running balance is pinned HERE —
   * days after it accumulate forward, days before it are reconstructed backward —
   * so a year-opening anchor ("saldo seisuga 01.01") places the balance where it
   * was observed instead of pretending the amount is today's balance.
   */
  anchorDate?: string;
  /** First day to emit; defaults to the earliest of any effective date / today. */
  from?: string;
  /** Last day to emit; defaults to the latest of any effective date / today. */
  to?: string;
  /** Reporting basis the placements resolve under; defaults to `cashBasis`. */
  basis?: Basis;
}

const DAY_MS = 86_400_000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function assertISO(value: string, label: string): void {
  if (!ISO_DATE.test(value)) {
    throw new RangeError(`${label}: expected an ISO date (YYYY-MM-DD), received ${value}`);
  }
}

function toUTC(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

function fromUTC(ms: number): string {
  const dt = new Date(ms);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The ISO day `n` days after `iso` (n may be negative). */
export function addDays(iso: string, n: number): string {
  return fromUTC(toUTC(iso) + n * DAY_MS);
}

/**
 * The fields the overdue-age read needs. `originExpectedDate` is the ORIGINAL due
 * day a reschedule/split preserved (nullable and optional so a plain
 * {@link CashFlowEntry} — which does not carry it — still satisfies the type).
 */
export interface OverdueAgeable {
  /** Current due day, ISO `YYYY-MM-DD`. */
  expectedDate: string;
  /** Original due day preserved across reschedules/splits, or null/absent. */
  originExpectedDate?: string | null;
}

/**
 * The day an entry has been overdue SINCE: the ORIGINAL due day. A rescheduled or
 * split document keeps its origin (`originExpectedDate`), so "how long has this
 * been pushed forward" is always measured from the FIRST deadline the document
 * ever had — never from the latest postponement. Falls back to `expectedDate`
 * when no origin is recorded (the entry was never moved).
 */
export function overdueSinceOf(e: OverdueAgeable): string {
  return e.originExpectedDate ?? e.expectedDate;
}

/**
 * Whole days `today` is past the ORIGINAL due day ({@link overdueSinceOf}) —
 * the register / hover "üle tähtaja N p" badge figure. Clamped at 0 for a
 * document that is not yet past its origin (display never shows a negative
 * age). Pure ISO-date arithmetic (UTC midnights), so DST can never skew the
 * count. All date math lives here in finance-core (master rule).
 */
export function overdueDaysOf(e: OverdueAgeable, today: string): number {
  const since = overdueSinceOf(e);
  assertISO(since, 'overdueDaysOf: since');
  assertISO(today, 'overdueDaysOf: today');
  return Math.max(0, Math.round((toUTC(today) - toUTC(since)) / DAY_MS));
}

function zeroSum(): MoneySum {
  return { net: 0, vat: 0, gross: 0 };
}

function addMoney(into: MoneySum, m: Money): void {
  into.net += m.net;
  into.vat += m.vat;
  into.gross += m.gross;
}

function addSum(into: MoneySum, s: MoneySum): void {
  into.net += s.net;
  into.vat += s.vat;
  into.gross += s.gross;
}

function zeroBuckets(): CertaintyBuckets {
  return { actual: zeroSum(), committed: zeroSum(), forecast: zeroSum(), total: zeroSum() };
}

/**
 * The reporting basis a placement is resolved under (glossary: `cashBasis` /
 * `accrualBasis` — replit.md terminology). `cashBasis` (the default everywhere) is
 * the treasury view: settled money on its payment date, open overdue carried to
 * today. `accrualBasis` is the bookkeeping lens: every document rests on its OWN
 * document (issue) date with its RAW money — no settlement split, no prepayment
 * netting, no carry-forward — so the window reconciles cent-for-cent with an
 * accrual report (Erply "Müügi koond"). ONE definition here in finance-core; the
 * lenses only pass it through (master rule + L14).
 */
export type Basis = 'cashBasis' | 'accrualBasis';

/**
 * Is a document PART of the accrual view at all? A prepayment invoice is a cash
 * event (money asked for before any revenue exists) — an accrual report never
 * counts it, and including it is exactly the "why doesn't this match Müügi
 * koond" confusion. The ONE exclusion predicate the accrual lens applies (in the
 * orchestrator, before every lens — L14), never an inline `docType !==` in a
 * component (master rule: one truth). Entries without a docType (manual payroll /
 * loans / taxes / forecasts) stay relevant — they are genuine accrual costs.
 */
export function accrualRelevant(entry: { docType?: string | null }): boolean {
  return entry.docType !== 'prepayment';
}

/**
 * Is a prepayment invoice UNCERTAIN forecast cash flow — an income-side ETT with
 * no cash received AND no final invoice or credit note made yet (glossary:
 * "katteta ettemaks")? Such a document merely ASKS for money: nothing has
 * arrived, nothing downstream exists, so its expectation may never materialise
 * and the operator can exclude it from the projection (the `Ettemaksud:
 * Kindlad` scope). Any settled cent ({@link settledAmountOf} — cash, adjustment
 * or document offset) or ANY link (a final invoice or kreeditarve exists, even
 * unpaid — netting already prevents a double count) makes it certain enough to
 * keep. Cost-side prepayments are NEVER uncertain — hiding our own payment
 * obligations would flatter the projection. The ONE exclusion predicate, applied
 * once in the orchestrator before every lens (L14), never an inline `docType`
 * check in a component (master rule: one truth).
 */
export function uncertainPrepayment(
  e: SettleableEntry & { docType?: string | null; kind: CashFlowKind },
): boolean {
  if (e.docType !== 'prepayment' || e.kind !== 'income') return false;
  if ((e.relatedDocuments ?? []).length > 0) return false;
  return settledAmountOf(e) <= 0;
}

/**
 * Does a prepayment invoice need the operator's REVIEW — a katteta ettemaks
 * ({@link uncertainPrepayment}: income-side ETT, no cash received, no final
 * invoice or credit note) that is ALSO past its due day ({@link
 * settlementStatusOf} = `overdue`)? A fresh ETT still inside its term is normal
 * business (invoice sent, money on its way) and is NOT flagged; once the due day
 * passes with nothing arrived and nothing issued downstream, the expectation is
 * stale — the deal may have died or the lõpparve was made without the link — and
 * the operator must check it. Drives the register / calendar "kontrolli" marker
 * (glossary: "katteta ettemaks"). The ONE definition of the flag, computed here
 * and only rendered by the lenses (master rule: one truth; L14).
 */
export function prepaymentReviewNeeded(
  e: SettleableEntry & { docType?: string | null; kind: CashFlowKind },
  today: string,
): boolean {
  return uncertainPrepayment(e) && settlementStatusOf(e, today) === 'overdue';
}

/**
 * An entry as it enters date placement: the math model plus the OPTIONAL settlement
 * facts {@link resolveEntry} reads to decide whether it has actually moved. Every
 * extra member is optional, so a plain {@link CashFlowEntry} still satisfies the type
 * and placement is unchanged when nothing is known about settlement (backward
 * compatible for every existing caller). The fields mirror {@link SettleableEntry}
 * but stay optional here because a test / mutation-acknowledgement entry may omit
 * them; the calendar + treasury reads always populate them from the view.
 */
export type PlaceableEntry = CashFlowEntry &
  PrepaymentOffsettable &
  SourceAwareSettleable & {
    /** Event-sourced cash paid, integer cents — used only when `sources` is absent. */
    paidCents?: number;
    /** Settlement adjustments (write-off / factoring), integer cents. */
    adjustedCents?: number;
    /** Document (issue) date — where the entry rests under `accrualBasis`. */
    documentDate?: string | null;
    /** Document type; `prepayment` is excluded from the accrual view. */
    docType?: string | null;
  };

/**
 * The cash amount settlement placement reasons about — the per-source breakdown is
 * the truth when present (the WINNING source's cash, never a cross-source sum);
 * otherwise the event-sourced `paidCents`. Mirrors `paidAmountOf` so "where it
 * lands" ({@link resolveEntry}) and "how much moved" ({@link resolvePlacements})
 * always agree on the same number (L9). Shared so the two can never drift.
 */
function effectiveCashOf(entry: PlaceableEntry): number {
  return entry.sources && entry.sources.length > 0
    ? effectivePaidAmountOf(entry) - prepaymentEchoCashOf(entry)
    : entry.paidCents ?? 0;
}

// ------------------------------------------------------------- KMD settlement

const ISO_MONTH = /^\d{4}-\d{2}$/;

/**
 * The EFFECTIVE settlement of a KMD entry — the canonical cents math the KMD
 * placement resolver reasons about. Lives HERE (not in kmdForecast) so
 * {@link resolvePlacements} / {@link resolveEntry} can delegate `tax:kmd`
 * entries carrying {@link KmdSettlement} without a circular import: the primary
 * rollup, register, simulation and export all read the SAME slices as the KMD
 * info row (master rule: one truth; L14). kmdForecast's `summarizeKmdSettlement`
 * builds its display summary (status + variance) on TOP of this. Pure; L9.
 *
 * Validates the ISO month and, for EVERY payment (superseded or not), a positive
 * integer `amountCents` and an ISO `paidOn`, so a malformed event surfaces loudly
 * instead of silently skewing the projection. Superseded payments (non-null
 * `supersededByPaymentId`) are excluded from the effective actual sum but retained
 * in the input for display metadata. `actualCents` is never clipped to the forecast
 * (above-forecast actual counts in full); `remainingForecastCents` is
 * `finalized ? 0 : max(forecastCents − actualCents, 0)`.
 */
export interface KmdEffectiveSettlement {
  /** Sum of effective (non-superseded) installments, integer cents (uncapped). */
  actualCents: number;
  /** `finalized ? 0 : max(forecastCents − actualCents, 0)`, integer cents. */
  remainingForecastCents: number;
  /** True when any effective payment came from LHV. */
  hasLhv: boolean;
  /** True when any effective payment came from a human (manual). */
  hasManual: boolean;
}

/**
 * Compute the {@link KmdEffectiveSettlement} of a settlement against the
 * immutable forecast baseline. The ONE place KMD effective cents are derived
 * (master rule; L9). Pure and deterministic.
 */
export function kmdEffectiveSettlement(
  settlement: KmdSettlement,
  forecastCents: number,
): KmdEffectiveSettlement {
  if (!ISO_MONTH.test(settlement.month)) {
    throw new RangeError(
      `kmdEffectiveSettlement: expected an ISO month (YYYY-MM), received ${settlement.month}`,
    );
  }
  if (!Number.isInteger(forecastCents)) {
    throw new RangeError('kmdEffectiveSettlement: forecastCents must be an integer');
  }

  let actualCents = 0;
  let hasLhv = false;
  let hasManual = false;

  for (const p of settlement.payments) {
    // Validate every payment regardless of supersession status (malformed data
    // must surface, never be silently skipped).
    if (!ISO_DATE.test(p.paidOn)) {
      throw new RangeError(
        `kmdEffectiveSettlement: expected an ISO paidOn (YYYY-MM-DD), received ${p.paidOn}`,
      );
    }
    if (!Number.isInteger(p.amountCents) || p.amountCents <= 0) {
      throw new RangeError(
        `kmdEffectiveSettlement: amountCents must be a positive integer, received ${p.amountCents}`,
      );
    }
    if (p.supersededByPaymentId !== null) continue;
    actualCents += p.amountCents;
    if (p.source === 'lhv') hasLhv = true;
    if (p.source === 'manual') hasManual = true;
  }

  const remainingForecastCents = settlement.finalized
    ? 0
    : Math.max(forecastCents - actualCents, 0);

  return { actualCents, remainingForecastCents, hasLhv, hasManual };
}

/** Exempt-style Money the KMD placements carry (net === gross, vat 0 — L9). */
function kmdExemptMoney(cents: number): Money {
  return { net: cents, vat: 0, gross: cents, vatRate: 0, vatMode: 'exempt' };
}

/**
 * Resolve a `tax:kmd` entry carrying {@link KmdSettlement} into its dated
 * placement slices — the canonical KMD placement logic, HERE so every projection
 * aggregator ({@link resolvePlacements}, {@link rollupDays}, {@link
 * aggregateByLine}, {@link aggregateLineParties}, the register / simulation /
 * export sums, and kmdForecast's `kmdDueFlow`) shares the SAME slices and can
 * never drift (master rule: one truth; L14). kmdForecast re-exports this.
 *
 * `entry.money` stays the immutable forecast baseline. Under `cashBasis`:
 *  - every EFFECTIVE (non-superseded) installment → its OWN `actual`, exempt-money
 *    placement on its own `paidOn` date; mixed manual + LHV installments ADD;
 *  - the remaining forecast (`kmdEffectiveSettlement.remainingForecastCents`) → ONE
 *    placement pinned to the original `entry.expectedDate` (the KMD due date),
 *    carrying the entry's own certainty, even when overdue — NEVER carried to
 *    today; finalized removes it even when actual < forecast;
 *  - above-forecast actual is never clipped (the actual slices report it in full).
 *
 * Under `accrualBasis` a single placement of the immutable baseline sits at the
 * entry's document/expected date with the entry's own certainty, settlement
 * ignored — the same rule as every other accrual entry. Integer cents (L9); pure.
 */
export function resolveKmdPlacements(
  entry: CashFlowEntry & { kmdSettlement: KmdSettlement; documentDate?: string | null },
  today: string,
  basis: Basis = 'cashBasis',
): EntryPlacement[] {
  if (!ISO_DATE.test(today)) {
    throw new RangeError(
      `resolveKmdPlacements: expected an ISO today (YYYY-MM-DD), received ${today}`,
    );
  }

  // Accrual basis: single immutable-baseline placement at the document/due date.
  if (basis === 'accrualBasis') {
    return [
      {
        date: entry.documentDate ?? entry.expectedDate,
        certainty: entry.certainty,
        carriedForward: false,
        money: entry.money,
      },
    ];
  }

  const { remainingForecastCents } = kmdEffectiveSettlement(
    entry.kmdSettlement,
    entry.money.gross,
  );
  const placements: EntryPlacement[] = [];

  // One actual, exempt-money placement per effective installment.
  for (const p of entry.kmdSettlement.payments) {
    if (p.supersededByPaymentId !== null) continue;
    placements.push({
      date: p.paidOn,
      certainty: 'actual',
      carriedForward: false,
      money: kmdExemptMoney(p.amountCents),
    });
  }

  // One remaining-forecast placement pinned to the original due date (never carried).
  if (remainingForecastCents > 0) {
    placements.push({
      date: entry.expectedDate,
      certainty: entry.certainty,
      carriedForward: false,
      money: kmdExemptMoney(remainingForecastCents),
    });
  }

  return placements;
}

/**
 * Does a `tax:kmd` entry carrying {@link KmdSettlement} still have an OPEN
 * (unpaid, non-finalized) remainder — i.e. does its KMD placement set still hold
 * a forecast slice at the due date? A finalized declaration, one fully paid (or
 * over-paid), and one with no remainder are all CLOSED. The ONE predicate
 * {@link resolveEntry} / {@link forecastPending} use to keep KMD certainty honest.
 */
function kmdHasOpenRemainder(entry: CashFlowEntry & { kmdSettlement: KmdSettlement }): boolean {
  return (
    kmdEffectiveSettlement(entry.kmdSettlement, entry.money.gross).remainingForecastCents > 0
  );
}

/**
 * Resolve where an entry lands on the calendar. ISO date strings compare
 * chronologically with `<`, so the past/future split is a plain string compare.
 *
 * A paid entry counts on the day the money actually MOVED, never on today. The legacy
 * per-entry `actualDate` dates a binary mark-paid, but every SYNCED payment (Erply /
 * SmartAccounts / LHV) is event-sourced — it never sets `actualDate` and instead
 * reaches us as `sources` / `paidCents`. Resolving purely on `actualDate` would pile
 * every paid-but-synced invoice onto today as if overdue; so a settled entry is placed
 * on its payment date (source-priority truth), a settled-without-a-date entry rests on
 * its expected date, and neither is carried forward. The cash amount is resolved the
 * SAME way as the settlement truth — the winning source's cash when the per-source
 * breakdown is present, else the event sum — never a cross-source sum (no double-count,
 * L9). All settlement/date math lives here in finance-core (master rule).
 *
 * Under `accrualBasis` the document rests on its OWN document (issue) date —
 * `documentDate`, falling back to `expectedDate` for a manual/forecast entry that
 * has none — with its OWN certainty and NEVER carried forward: settlement is a
 * cash concept, so payment state cannot move an accrual placement (one truth —
 * {@link Basis}).
 */
export function resolveEntry(
  entry: PlaceableEntry,
  today: string,
  basis: Basis = 'cashBasis',
): ResolvedEntry {
  if (basis === 'accrualBasis') {
    return {
      ...entry,
      effectiveDate: entry.documentDate ?? entry.expectedDate,
      effectiveCertainty: entry.certainty,
      carriedForward: false,
    };
  }

  // A `tax:kmd` entry carrying settlement metadata resolves off its KMD placements
  // (one truth with resolveKmdPlacements): an OPEN remainder (partial, not finalized)
  // stays forecast/committed pinned to the due date — never carried to today; a
  // CLOSED declaration (finalized, or fully/over-paid) is no longer pending —
  // it lands `actual` on its latest effective payment date (or the due date when
  // finalized with no payments). This keeps forecastPending honest for KMD rows.
  if (entry.kmdSettlement) {
    const kmd = entry as CashFlowEntry & { kmdSettlement: KmdSettlement };
    if (kmdHasOpenRemainder(kmd)) {
      return {
        ...entry,
        effectiveDate: entry.expectedDate,
        effectiveCertainty: entry.certainty,
        carriedForward: false,
      };
    }
    // Closed (finalized, or fully/over-paid): the obligation no longer contributes
    // an open forecast, so it is NEVER pending. It lands `actual` on its latest
    // effective payment date; a finalized-with-no-payments row (no cash ever moved)
    // rests `actual` on its due date — closed, not forecast.
    let latestPaidOn: string | null = null;
    for (const p of entry.kmdSettlement.payments) {
      if (p.supersededByPaymentId !== null) continue;
      if (latestPaidOn === null || p.paidOn > latestPaidOn) latestPaidOn = p.paidOn;
    }
    return {
      ...entry,
      effectiveDate: latestPaidOn ?? entry.expectedDate,
      effectiveCertainty: 'actual',
      carriedForward: false,
    };
  }

  // A legacy binary mark-paid already dates the movement on its own actual date.
  if (entry.actualDate) {
    return {
      ...entry,
      effectiveDate: entry.actualDate,
      effectiveCertainty: 'actual',
      carriedForward: false,
    };
  }

  const gross = entry.money.gross;
  const offset = totalDocumentOffsetOf(entry);
  const cash = effectiveCashOf(entry);
  const adjusted = entry.adjustedCents ?? 0;

  // Cash that covers the prepayment-netted gross is an ACTUAL movement → it lands on
  // its payment date with certainty `actual`, exactly like a legacy actualDate. Gated
  // against gross − offset because the projection contributes the prepayment-netted
  // money (effectiveCashFlowMoneyOf), so that remainder is what truly arrived that
  // day. Compared in the document's own direction (settlementDirectionOf): a credit
  // note is stored negative and its refund arrives as NEGATIVE cash, so a fully
  // refunded credit note lands on its refund date too — never carried to today.
  const dir = settlementDirectionOf(gross);
  if (gross !== 0 && dir * cash > 0 && dir * cash >= dir * (gross - offset)) {
    const paidOn = effectivePaymentSourceOf(entry)?.latestPaidOn ?? null;
    if (paidOn) {
      return { ...entry, effectiveDate: paidOn, effectiveCertainty: 'actual', carriedForward: false };
    }
  }

  // Fully settled but with no cash date to sit on — a received prepayment covers it,
  // or it was closed by an adjustment (bad-debt write-off / factoring). Nothing is
  // left to collect, so it is NOT overdue / carried: it rests on its expected date
  // with its own certainty. Subsumes the former fully-prepaid branch (same result).
  if (gross !== 0 && dir * (cash + adjusted + offset) >= dir * gross) {
    return {
      ...entry,
      effectiveDate: entry.expectedDate,
      effectiveCertainty: entry.certainty,
      carriedForward: false,
    };
  }

  if (entry.expectedDate < today) {
    // Overdue and still owed → carried forward to today (not left in the past).
    return {
      ...entry,
      effectiveDate: today,
      effectiveCertainty: entry.certainty,
      carriedForward: true,
    };
  }

  return {
    ...entry,
    effectiveDate: entry.expectedDate,
    effectiveCertainty: entry.certainty,
    carriedForward: false,
  };
}

/**
 * Does an entry still contribute PLAN money to the projection — its RESOLVED
 * certainty ({@link resolveEntry}, so a paid/moved document reads `actual`, never
 * its stored label) is `forecast`? The ONE exclusion predicate of the primary
 * view's "Prognoos: väljas" scope, applied once in the orchestrator before every
 * lens (L14) — never an inline `certainty ===` check in a component (master
 * rule: one truth).
 */
export function forecastPending(entry: PlaceableEntry, today: string, basis: Basis = 'cashBasis'): boolean {
  return resolveEntry(entry, today, basis).effectiveCertainty === 'forecast';
}

/**
 * One dated slice of an entry's money as it hits the projection. Most entries
 * produce exactly ONE placement (their {@link resolveEntry} spot carrying their
 * {@link effectiveCashFlowMoneyOf}); a PARTIALLY-paid document produces TWO — see
 * {@link resolvePlacements}.
 */
export interface EntryPlacement {
  /** The day this slice contributes to. */
  date: string;
  /** The certainty this slice contributes as (`actual` for cash that moved). */
  certainty: Certainty;
  /** True when this slice is the overdue remainder rolled forward to today. */
  carriedForward: boolean;
  /** The slice's money (integer cents — L9). */
  money: Money;
}

/**
 * Resolve an entry into its dated money slices — the ONE bridge between "where it
 * lands" ({@link resolveEntry}) and "how much" ({@link effectiveCashFlowMoneyOf}),
 * used by every projection aggregator (master rule; L9).
 *
 * Most entries yield ONE placement: their resolved date/certainty carrying their
 * prepayment-netted money (identical to the previous resolveEntry ×
 * effectiveCashFlowMoneyOf pairing — zero behavior change for unpaid, fully-settled
 * or legacy mark-paid entries).
 *
 * A PARTIALLY-paid document (0 < cash < prepayment-netted gross, compared in the
 * document's own direction — a partially REFUNDED credit note splits the same way,
 * both slices negative) yields TWO:
 *  1. the PAID slice — cash that actually arrived — as an `actual` movement on the
 *     winning source's `latestPaidOn` (a dateless event-sourced partial rests on the
 *     expected date, mirroring the settled-without-a-date rule);
 *  2. the OUTSTANDING slice (gross − cash − adjustments) at the document's resolved
 *     place — carried to today when overdue, so the "Üle tähtaja" bucket now equals
 *     the true jääk (Σ {@link outstandingOf}), never the full invoice.
 * Without the split, a 94%-paid overdue invoice would land on today with its FULL
 * money, inflating both the overdue bucket and the current month's flow with cash
 * that is already inside the bank-anchored opening balance. An adjusted (written-off
 * / factored) sliver lands in NO placement — it never hits the bank. Multiple
 * partial payments collapse onto the single known `latestPaidOn` (an intra-period
 * approximation; the per-payment dates are not modeled). Both slices split via
 * {@link splitMoney}, so paid + outstanding + adjusted reconstitute the netted money
 * cent-for-cent with VAT re-derived per slice (L9). Pure and deterministic.
 *
 * Under `accrualBasis` every document yields exactly ONE slice at its accrual spot
 * ({@link resolveEntry}) carrying its RAW `money` — never the prepayment-netted
 * {@link effectiveCashFlowMoneyOf} and never a paid/outstanding split: netting and
 * settlement are cash concepts, and netting a final invoice would under-report the
 * window against an accrual report (Erply "Müügi koond"). A credit note is stored
 * negative, so it subtracts at its own document date exactly as the report does.
 */
export function resolvePlacements(
  entry: PlaceableEntry,
  today: string,
  basis: Basis = 'cashBasis',
): EntryPlacement[] {
  // A `tax:kmd` entry carrying settlement metadata is resolved by the canonical
  // KMD placement logic — the SAME slices kmdForecast's `kmdDueFlow` sums, so the
  // primary rollup / register / simulation / export never drift from the KMD info
  // row (master rule; L14). Handles BOTH bases (accrual → immutable baseline at the
  // document/due date; cash → per-installment actuals + a due-date-pinned remainder).
  if (entry.kmdSettlement) {
    return resolveKmdPlacements(
      entry as CashFlowEntry & { kmdSettlement: KmdSettlement },
      today,
      basis,
    );
  }

  if (basis === 'accrualBasis') {
    const a = resolveEntry(entry, today, basis);
    return [
      {
        date: a.effectiveDate,
        certainty: a.effectiveCertainty,
        carriedForward: false,
        money: entry.money,
      },
    ];
  }

  const r = resolveEntry(entry, today);
  const effective = effectiveCashFlowMoneyOf(entry);

  const cash = effectiveCashOf(entry);
  const adjusted = entry.adjustedCents ?? 0;
  const dir = settlementDirectionOf(effective.gross);
  const partiallyPaid =
    !entry.actualDate &&
    effective.gross !== 0 &&
    dir * cash > 0 &&
    dir * (cash + adjusted) < dir * effective.gross;

  if (!partiallyPaid) {
    return [
      {
        date: r.effectiveDate,
        certainty: r.effectiveCertainty,
        carriedForward: r.carriedForward,
        money: effective,
      },
    ];
  }

  const paidOn = effectivePaymentSourceOf(entry)?.latestPaidOn ?? entry.expectedDate;
  return [
    {
      date: paidOn,
      certainty: 'actual',
      carriedForward: false,
      money: splitMoney(effective, cash).moved,
    },
    {
      date: r.effectiveDate,
      certainty: r.effectiveCertainty,
      carriedForward: r.carriedForward,
      money: splitMoney(effective, cash + adjusted).remainder,
    },
  ];
}

function minISO(values: string[]): string {
  return values.reduce((m, x) => (x < m ? x : m));
}

function maxISO(values: string[]): string {
  return values.reduce((m, x) => (x > m ? x : m));
}

/**
 * Roll a flat list of entries up into per-day cash-flow days with a running
 * balance. Pure: same inputs always produce the same output.
 */
export function rollupDays(entries: PlaceableEntry[], options: RollupOptions): DayRollup[] {
  const { today, openingBalance } = options;
  assertISO(today, 'today');
  if (!Number.isSafeInteger(openingBalance)) {
    throw new RangeError(`openingBalance: expected whole cents, received ${openingBalance}`);
  }
  const anchorDate = options.anchorDate ?? today;
  assertISO(anchorDate, 'anchorDate');

  // Each entry contributes its resolvePlacements slices: netted by any RECEIVED
  // prepayment offset, and a partially-paid document split into its paid (actual,
  // on the payment date) and outstanding (resolved date, carried when overdue)
  // slices — so the overdue bucket holds only the true jääk (L9 — all the money/date
  // math lives in finance-core, never a component). Under `accrualBasis` each entry
  // is ONE raw-money slice at its document date (see resolvePlacements).
  const basis = options.basis ?? 'cashBasis';
  const resolved = entries.flatMap((e) =>
    resolvePlacements(e, today, basis).map((p) => ({ kind: e.kind, ...p })),
  );
  const effectiveDates = resolved.map((r) => r.date);

  const from = options.from ?? minISO([...effectiveDates, today]);
  const to = options.to ?? maxISO([...effectiveDates, today]);
  assertISO(from, 'from');
  assertISO(to, 'to');

  const dayMap = new Map<string, DayRollup>();
  const ensure = (date: string): DayRollup => {
    let day = dayMap.get(date);
    if (!day) {
      day = {
        date,
        income: zeroBuckets(),
        cost: zeroBuckets(),
        net: zeroSum(),
        carriedForwardIncome: zeroSum(),
        carriedForwardCost: zeroSum(),
        runningBalance: 0,
        confirmedRunningBalance: 0,
      };
      dayMap.set(date, day);
    }
    return day;
  };

  // Seed the contiguous output range (so empty days still render), today, and the
  // anchor day (the balance pin) even when they hold no entries.
  if (from <= to) {
    for (let d = from; d <= to; d = addDays(d, 1)) ensure(d);
  }
  ensure(today);
  ensure(anchorDate);

  for (const r of resolved) {
    const day = ensure(r.date);
    const buckets = r.kind === 'income' ? day.income : day.cost;
    addMoney(buckets[r.certainty], r.money);
    addMoney(buckets.total, r.money);
    if (r.carriedForward) {
      addMoney(r.kind === 'income' ? day.carriedForwardIncome : day.carriedForwardCost, r.money);
    }
  }

  for (const day of dayMap.values()) {
    day.net = {
      net: day.income.total.net - day.cost.total.net,
      vat: day.income.total.vat - day.cost.total.vat,
      gross: day.income.total.gross - day.cost.total.gross,
    };
  }

  // Running balance (gross), pinned at `anchorDate` by openingBalance (the
  // balance at the START of the anchor day — a year-opening "saldo seisuga
  // 01.01" anchors January, not today):
  //  - the anchor day and forward: project by adding each day's net.
  //  - before the anchor day: reconstruct by walking back from the anchor.
  // The confirmed twin runs in the SAME passes over the same buckets, skipping
  // each day's forecast money (net minus the forecast buckets, per direction) —
  // one truth for "kinnitatud saldo", never a second rollup elsewhere.
  const sortedDays = [...dayMap.keys()].sort();
  const confirmedNetGross = (day: DayRollup): number =>
    day.net.gross - day.income.forecast.gross + day.cost.forecast.gross;

  let forward = openingBalance;
  let forwardConfirmed = openingBalance;
  for (const d of sortedDays) {
    if (d < anchorDate) continue;
    const day = dayMap.get(d)!;
    forward += day.net.gross;
    forwardConfirmed += confirmedNetGross(day);
    day.runningBalance = forward;
    day.confirmedRunningBalance = forwardConfirmed;
  }

  let backward = openingBalance;
  let backwardConfirmed = openingBalance;
  for (let i = sortedDays.length - 1; i >= 0; i--) {
    const d = sortedDays[i];
    if (d >= anchorDate) continue;
    const day = dayMap.get(d)!;
    day.runningBalance = backward;
    day.confirmedRunningBalance = backwardConfirmed;
    backward -= day.net.gross;
    backwardConfirmed -= confirmedNetGross(day);
  }

  const out: DayRollup[] = [];
  if (from <= to) {
    for (let d = from; d <= to; d = addDays(d, 1)) out.push(dayMap.get(d)!);
  }
  return out;
}

/** Granularity the treasury table is viewed at. */
export type Granularity = 'day' | 'week' | 'month' | 'quarter';

/** A contiguous span of days summed into one column of the treasury table. */
export interface PeriodRollup {
  /** Stable key: the day, the Monday of the ISO week, or `YYYY-MM`. */
  key: string;
  /** First and last day actually present in this period (within the range). */
  from: string;
  to: string;
  income: MoneySum;
  cost: MoneySum;
  /** income − cost, per field. */
  net: MoneySum;
  carriedForwardIncome: MoneySum;
  carriedForwardCost: MoneySum;
  /** carriedForwardIncome − carriedForwardCost, per field (the overdue net). */
  carriedForwardNet: MoneySum;
  /** The forecast slice of `income` (Σ of the days' income.forecast buckets) —
   *  the matrix "sh prognoos" sub-line reads it, never re-summing (master rule). */
  incomeForecast: MoneySum;
  /** The forecast slice of `cost` (Σ of the days' cost.forecast buckets). */
  costForecast: MoneySum;
  /** incomeForecast − costForecast, per field (the forecast slice of `net`). */
  netForecast: MoneySum;
  /** Gross balance at the start of the period (before the first day's movement). */
  openingBalance: number;
  /** Gross balance at the end of the period (the last day's running balance). */
  closingBalance: number;
  /** The confirmed-only twin of `closingBalance` (the last day's
   *  {@link DayRollup.confirmedRunningBalance}) — equals `closingBalance` when the
   *  window up to here holds no forecast money. */
  confirmedClosingBalance: number;
}

/** Monday-based ISO-week start for an ISO day. */
function weekStart(iso: string): string {
  const dow = new Date(toUTC(iso)).getUTCDay(); // 0=Sun … 6=Sat
  const sinceMonday = (dow + 6) % 7;
  return addDays(iso, -sinceMonday);
}

function periodKey(iso: string, gran: Granularity): string {
  if (gran === 'week') return weekStart(iso);
  if (gran === 'month') return iso.slice(0, 7);
  if (gran === 'quarter') {
    const month = Number(iso.slice(5, 7));
    const quarter = Math.floor((month - 1) / 3) + 1;
    return `${iso.slice(0, 4)}-Q${quarter}`;
  }
  return iso;
}

/**
 * Balance at the START of the visible window, derived from the FIRST rolled-up
 * day (its end-of-day running balance minus its own net) — the ONE place display
 * consumers get the window-start "Algsaldo" (master rule: no component does its
 * own balance math). The rollup's `openingBalance` INPUT is pinned at the
 * anchor's as-of date, which with a past anchor and a narrower window is NOT the
 * window start, so consumers must never show it directly. An empty window falls
 * back to the provided anchor-pinned opening.
 */
export function windowOpeningBalance(days: DayRollup[], fallback: number): number {
  const first = days[0];
  return first ? first.runningBalance - first.net.gross : fallback;
}

/**
 * Group already-rolled-up days into period columns (day / week / month) for the
 * treasury table — the ONE place period sums live (master rule: no component
 * sums money itself). Input days are assumed ascending and contiguous as emitted
 * by `rollupDays`; sums are integer cents, balances taken from the day running
 * balance so they stay exact.
 */
export function aggregatePeriods(days: DayRollup[], gran: Granularity): PeriodRollup[] {
  const order: string[] = [];
  const map = new Map<string, PeriodRollup>();
  for (const day of days) {
    const key = periodKey(day.date, gran);
    let period = map.get(key);
    if (!period) {
      period = {
        key,
        from: day.date,
        to: day.date,
        income: zeroSum(),
        cost: zeroSum(),
        net: zeroSum(),
        carriedForwardIncome: zeroSum(),
        carriedForwardCost: zeroSum(),
        carriedForwardNet: zeroSum(),
        incomeForecast: zeroSum(),
        costForecast: zeroSum(),
        netForecast: zeroSum(),
        // Start-of-first-day balance = its end-of-day balance minus that day's net.
        openingBalance: day.runningBalance - day.net.gross,
        closingBalance: day.runningBalance,
        confirmedClosingBalance: day.confirmedRunningBalance,
      };
      map.set(key, period);
      order.push(key);
    }
    period.to = day.date;
    addSum(period.income, day.income.total);
    addSum(period.cost, day.cost.total);
    addSum(period.net, day.net);
    addSum(period.carriedForwardIncome, day.carriedForwardIncome);
    addSum(period.carriedForwardCost, day.carriedForwardCost);
    addSum(period.incomeForecast, day.income.forecast);
    addSum(period.costForecast, day.cost.forecast);
    period.closingBalance = day.runningBalance;
    period.confirmedClosingBalance = day.confirmedRunningBalance;
  }
  // The overdue net is income − cost of the carried-forward sums, per field — the
  // ONE place this subtotal lives (master rule: no component sums money itself).
  // The forecast net is the same subtraction over the forecast slices.
  for (const period of map.values()) {
    period.carriedForwardNet = {
      net: period.carriedForwardIncome.net - period.carriedForwardCost.net,
      vat: period.carriedForwardIncome.vat - period.carriedForwardCost.vat,
      gross: period.carriedForwardIncome.gross - period.carriedForwardCost.gross,
    };
    period.netForecast = {
      net: period.incomeForecast.net - period.costForecast.net,
      vat: period.incomeForecast.vat - period.costForecast.vat,
      gross: period.incomeForecast.gross - period.costForecast.gross,
    };
  }
  return order.map((k) => map.get(k)!);
}

/**
 * One cash-flow line after rollup: its income + cost certainty buckets and net.
 * (replit.md module 1.3 — the 7-line breakdown of the aggregate Income/Cost rows.)
 */
export interface LineRollup {
  category: CashFlowCategory;
  income: CertaintyBuckets;
  cost: CertaintyBuckets;
  /** income.total − cost.total, per field. */
  net: MoneySum;
  /** Overdue income this line carried forward (→ today) inside the window — the
   *  per-line slice of `PeriodRollup.carriedForwardIncome`. Already INSIDE `income`
   *  (resolveEntry moved it to today), so it is a non-additive highlight, never a
   *  second sum (mirrors DayRollup/PeriodRollup; master rule: one truth per number). */
  carriedForwardIncome: MoneySum;
  /** Overdue cost this line carried forward (→ today) inside the window — the
   *  per-line slice of `PeriodRollup.carriedForwardCost`; already INSIDE `cost`. */
  carriedForwardCost: MoneySum;
  /** carriedForwardIncome − carriedForwardCost, per field (the line's overdue net). */
  carriedForwardNet: MoneySum;
}

export interface LineRollupOptions {
  /** Today, ISO `YYYY-MM-DD`, in the team time zone (Europe/Tallinn). */
  today: string;
  /** First effective day to include (inclusive); unbounded if omitted. */
  from?: string;
  /** Last effective day to include (inclusive); unbounded if omitted. */
  to?: string;
  /** Reporting basis the placements resolve under; defaults to `cashBasis`. */
  basis?: Basis;
}

/**
 * Break a flat list of entries into the 7 named cash-flow lines, each bucketed by
 * certainty (actual / committed / forecast). This is the ONE definition of the
 * per-line breakdown (master rule: no component sums money itself); the primary
 * view's matrix rows read it directly.
 *
 * It resolves each entry exactly like `rollupDays` (carry-forward of overdue
 * pending entries to `today`, actual-once-moved) and applies the same
 * effective-date window, so the per-line sums RECONCILE cent-for-cent with the
 * income / cost / net totals of `rollupDays` over the same window. Always returns
 * all 7 lines in canonical order (empty lines have zero buckets) so the matrix is
 * stable. Integer cents only (L9); pure and deterministic.
 */
export function aggregateByLine(
  entries: CashFlowEntry[],
  options: LineRollupOptions,
): LineRollup[] {
  const { today } = options;
  assertISO(today, 'today');
  if (options.from !== undefined) assertISO(options.from, 'from');
  if (options.to !== undefined) assertISO(options.to, 'to');

  const lines = new Map<CashFlowCategory, LineRollup>();
  for (const category of CASH_FLOW_CATEGORIES) {
    lines.set(category, {
      category,
      income: zeroBuckets(),
      cost: zeroBuckets(),
      net: zeroSum(),
      carriedForwardIncome: zeroSum(),
      carriedForwardCost: zeroSum(),
      carriedForwardNet: zeroSum(),
    });
  }

  for (const e of entries) {
    const line = lines.get(e.category);
    if (!line) {
      throw new RangeError(`aggregateByLine: unknown category ${e.category}`);
    }
    // The SAME placement slices as rollupDays (paid slice on its payment date,
    // outstanding slice at the resolved spot), so the per-line sums still reconcile
    // cent-for-cent with the daily rollup (L9). Each slice window-filters on its OWN
    // date — the paid part of a partially-paid overdue invoice counts in the month
    // the cash arrived, never in today's column. The same basis as the daily rollup,
    // so the matrix lines reconcile with the days under EITHER lens setting.
    for (const p of resolvePlacements(e, today, options.basis ?? 'cashBasis')) {
      if (options.from !== undefined && p.date < options.from) continue;
      if (options.to !== undefined && p.date > options.to) continue;
      const buckets = e.kind === 'income' ? line.income : line.cost;
      addMoney(buckets[p.certainty], p.money);
      addMoney(buckets.total, p.money);
      // The overdue (carried-forward) slice of this line, by direction — already
      // counted in income/cost above, so it stays a non-additive highlight (L9).
      if (p.carriedForward) {
        addMoney(e.kind === 'income' ? line.carriedForwardIncome : line.carriedForwardCost, p.money);
      }
    }
  }

  for (const line of lines.values()) {
    line.net = {
      net: line.income.total.net - line.cost.total.net,
      vat: line.income.total.vat - line.cost.total.vat,
      gross: line.income.total.gross - line.cost.total.gross,
    };
    line.carriedForwardNet = {
      net: line.carriedForwardIncome.net - line.carriedForwardCost.net,
      vat: line.carriedForwardIncome.vat - line.carriedForwardCost.vat,
      gross: line.carriedForwardIncome.gross - line.carriedForwardCost.gross,
    };
  }

  return CASH_FLOW_CATEGORIES.map((c) => lines.get(c)!);
}

/**
 * ONE certainty slice of a line's net (income[c] − cost[c], per field) — e.g.
 * `'forecast'` is the plan-only portion of the cell the matrix renders as the
 * "sh prognoos" sub-line. Lives here because {@link LineRollup.net} is only the
 * TOTAL subtraction: a component needing the sliced net must never derive it
 * with its own arithmetic (master rule; L9). Integer cents; pure.
 */
export function lineCertaintySlice(line: LineRollup, certainty: Certainty): MoneySum {
  return {
    net: line.income[certainty].net - line.cost[certainty].net,
    vat: line.income[certainty].vat - line.cost[certainty].vat,
    gross: line.income[certainty].gross - line.cost[certainty].gross,
  };
}

/** Window grand-total — the treasury matrix's leftmost "Kokku" column. */
export interface WindowTotal {
  income: MoneySum;
  cost: MoneySum;
  net: MoneySum;
  carriedForwardIncome: MoneySum;
  carriedForwardCost: MoneySum;
  /** carriedForwardIncome − carriedForwardCost, per field (the overdue net). */
  carriedForwardNet: MoneySum;
  /** The forecast slice of `income` across the window (Σ of the periods'). */
  incomeForecast: MoneySum;
  /** The forecast slice of `cost` across the window. */
  costForecast: MoneySum;
  /** incomeForecast − costForecast, per field (the forecast slice of `net`). */
  netForecast: MoneySum;
  openingBalance: number;
  closingBalance: number;
  /** The confirmed-only twin of `closingBalance` (the last period's). */
  confirmedClosingBalance: number;
}

/**
 * Sum period columns into one window total (the "Kokku" column of the treasury
 * matrix). The ONE place this subtotal lives (master rule: no component sums
 * money). Opening is the first period's opening balance, closing the last
 * period's closing balance; an empty window is all-zero. Integer cents (L9).
 */
export function sumPeriods(periods: PeriodRollup[]): WindowTotal {
  const income = zeroSum();
  const cost = zeroSum();
  const net = zeroSum();
  const carriedForwardIncome = zeroSum();
  const carriedForwardCost = zeroSum();
  const carriedForwardNet = zeroSum();
  const incomeForecast = zeroSum();
  const costForecast = zeroSum();
  const netForecast = zeroSum();
  for (const p of periods) {
    addSum(income, p.income);
    addSum(cost, p.cost);
    addSum(net, p.net);
    addSum(carriedForwardIncome, p.carriedForwardIncome);
    addSum(carriedForwardCost, p.carriedForwardCost);
    addSum(carriedForwardNet, p.carriedForwardNet);
    addSum(incomeForecast, p.incomeForecast);
    addSum(costForecast, p.costForecast);
    addSum(netForecast, p.netForecast);
  }
  return {
    income,
    cost,
    net,
    carriedForwardIncome,
    carriedForwardCost,
    carriedForwardNet,
    incomeForecast,
    costForecast,
    netForecast,
    openingBalance: periods.length > 0 ? periods[0].openingBalance : 0,
    closingBalance: periods.length > 0 ? periods[periods.length - 1].closingBalance : 0,
    confirmedClosingBalance:
      periods.length > 0 ? periods[periods.length - 1].confirmedClosingBalance : 0,
  };
}

// ----------------------------------------------------- counterparty breakdown

/** One counterparty's money in one period column: income / cost / net. */
export interface PartyCell {
  income: MoneySum;
  cost: MoneySum;
  /** income − cost, per field. */
  net: MoneySum;
}

/** A named counterparty row inside an expanded line: its per-column cells + total. */
export interface PartyRow {
  party: string;
  /** Where this party's entries came from (first-seen entry's source). */
  source: CashFlowSource;
  /** One cell per requested column, in column order. */
  cells: PartyCell[];
  /** The window total across all columns (== sum of `cells`). */
  total: PartyCell;
  /** The most urgent management priority among this party's in-window entries
   *  (PRIORITY_ORDER), or null — a non-monetary triage cue surfaced in EVERY lens
   *  (L14); attention parties are ranked ahead of the long tail, never buried in "Muud". */
  priority: Priority | null;
  /** How many of this party's in-window entries finance-core carried forward
   *  (overdue → today). A count, not money (L9 governs money math, not tallies). */
  overdueCount: number;
  /** The distinct project identifiers (tunnus) this party's in-window entries are
   *  linked to, sorted for determinism — a "surface projects" cue + ranking
   *  tie-break. Empty = no project. Both lenses show the single code when there is
   *  exactly one, else a "N projekti" rollup (L14). A list, not money (L9). */
  projectCodes: string[];
  /** Outstanding (unpaid) gross this party still owes / is owed across its in-window
   *  entries, integer cents — `Σ max(0, outstandingOf(e))` from finance-core, the
   *  default "what matters" emphasis (laekumata / tasumata) that drives the size
   *  ranking. The ONE place a party's unpaid exposure is summed (master rule; L9). */
  outstanding: number;
}

/** The rolled-up tail ("Muud (N)") of counterparties below the named top-N. */
export interface OthersRow {
  /** How many distinct counterparties are folded into this bucket. */
  count: number;
  cells: PartyCell[];
  total: PartyCell;
}

/** A line expanded into its counterparties: named top-N + a "Muud" rollup. */
export interface LinePartyMatrix {
  category: CashFlowCategory;
  parties: PartyRow[];
  /** null when there is no tail below the named top-N. */
  others: OthersRow | null;
}

export interface PartyMatrixOptions {
  /** Today, ISO `YYYY-MM-DD`, in the team time zone (Europe/Tallinn). */
  today: string;
  /** The line to expand. */
  category: CashFlowCategory;
  /** The period columns to break each counterparty across, in display order. */
  columns: { from: string; to: string }[];
  /** How many named counterparties to show before folding the rest into "Muud". */
  topN?: number;
  /** Reporting basis the placements resolve under; defaults to `cashBasis`. */
  basis?: Basis;
}

function zeroCell(): PartyCell {
  return { income: zeroSum(), cost: zeroSum(), net: zeroSum() };
}

function addToCell(cell: PartyCell, kind: CashFlowKind, m: Money): void {
  addMoney(kind === 'income' ? cell.income : cell.cost, m);
}

function finalizeCellNet(cell: PartyCell): void {
  cell.net = {
    net: cell.income.net - cell.cost.net,
    vat: cell.income.vat - cell.cost.vat,
    gross: cell.income.gross - cell.cost.gross,
  };
}

function columnIndexFor(date: string, columns: { from: string; to: string }[]): number {
  for (let i = 0; i < columns.length; i++) {
    if (columns[i].from <= date && date <= columns[i].to) return i;
  }
  return -1;
}

interface PartyAgg {
  party: string;
  source: CashFlowSource;
  cells: PartyCell[];
  total: PartyCell;
  priority: Priority | null;
  overdueCount: number;
  projectCodes: Set<string>;
  outstanding: number;
}

/**
 * Expand ONE cash-flow line into its counterparties across the given period
 * columns — the data behind the matrix's inline expandable sub-rows (module 1.1).
 * Each named party also carries non-monetary triage metadata — its most urgent
 * `priority`, its `overdueCount` (entries finance-core carried forward), its linked
 * `projectCodes` and its `outstanding` (unpaid) exposure — and the ranking surfaces
 * the parties needing ATTENTION first (a priority flag or overdue items), then by
 * urgency, then overdue volume, then project-linked, then by the biggest UNPAID
 * exposure (laekumata / tasumata — the default "what matters"), ties broken by name.
 * So the named top-N is a LOGICAL selection (important / overdue / projects / most
 * owed), never just the first by size, and the documents needing action are never
 * buried in "Muud". Priority + project ride in structurally (the math model omits
 * display-only metadata); `outstanding` is computed here via finance-core's
 * `outstandingOf` so no component subtracts paid from gross itself (master rule;
 * L9). The per-column cells RECONCILE cent-for-cent with `aggregateByLine` for the
 * same category over each column. Integer cents only (L9); pure and deterministic.
 */
export function aggregateLineParties<
  E extends CashFlowEntry &
    SettleableEntry & { priority?: Priority | null; project?: string | null },
>(entries: E[], options: PartyMatrixOptions): LinePartyMatrix {
  const { today, category, columns } = options;
  const topN = options.topN ?? 5;
  assertISO(today, 'today');
  if (!Number.isInteger(topN) || topN < 0) {
    throw new RangeError(`topN: expected a non-negative integer, received ${topN}`);
  }
  for (const col of columns) {
    assertISO(col.from, 'column.from');
    assertISO(col.to, 'column.to');
  }

  const map = new Map<string, PartyAgg>();
  const order: string[] = [];
  for (const e of entries) {
    if (e.category !== category) continue;
    // The SAME placement slices as rollupDays/aggregateByLine (paid slice on its
    // payment date, outstanding slice at the resolved spot), each landing in the
    // column ITS date falls into, so the party cells reconcile cent-for-cent with
    // the line totals (L9). An entry with no slice inside any column is skipped
    // entirely (no metadata row for an out-of-window document). Same basis as the
    // line totals, so the expanded sub-rows reconcile under either lens setting.
    const placed = resolvePlacements(e, today, options.basis ?? 'cashBasis')
      .map((p) => ({ p, col: columnIndexFor(p.date, columns) }))
      .filter(({ col }) => col !== -1);
    if (placed.length === 0) continue;
    let agg = map.get(e.party);
    if (!agg) {
      agg = {
        party: e.party,
        source: e.source,
        cells: columns.map(() => zeroCell()),
        total: zeroCell(),
        priority: null,
        overdueCount: 0,
        projectCodes: new Set<string>(),
        outstanding: 0,
      };
      map.set(e.party, agg);
      order.push(e.party);
    }
    for (const { p, col } of placed) {
      addToCell(agg.cells[col], e.kind, p.money);
      addToCell(agg.total, e.kind, p.money);
    }
    // Triage metadata (ONCE per document, never per slice): keep the most urgent
    // priority, tally carried-forward (overdue) documents, collect project links —
    // all non-monetary (L9). The unpaid exposure IS money, so it is summed via
    // finance-core's `outstandingOf` (never a bare gross−paid here); overpayments
    // clamp to 0 so they don't inflate the rank.
    if (e.priority && (agg.priority === null || PRIORITY_ORDER[e.priority] < PRIORITY_ORDER[agg.priority])) {
      agg.priority = e.priority;
    }
    if (placed.some(({ p }) => p.carriedForward)) agg.overdueCount += 1;
    if (e.project) agg.projectCodes.add(e.project);
    agg.outstanding += Math.max(0, outstandingOf(e));
  }

  for (const agg of map.values()) {
    agg.cells.forEach(finalizeCellNet);
    finalizeCellNet(agg.total);
  }

  const ranked = order
    .map((p) => map.get(p)!)
    .sort((a, b) => {
      // Attention first: any priority flag OR overdue items, so the named top-N
      // never buries the documents needing action down in the "Muud" tail.
      const aAtt = a.priority !== null || a.overdueCount > 0;
      const bAtt = b.priority !== null || b.overdueCount > 0;
      if (aAtt !== bAtt) return aAtt ? -1 : 1;
      // ...then most urgent priority (null sorts after all priorities)...
      const ap = a.priority ? PRIORITY_ORDER[a.priority] : PRIORITIES.length;
      const bp = b.priority ? PRIORITY_ORDER[b.priority] : PRIORITIES.length;
      if (ap !== bp) return ap - bp;
      // ...then the most overdue documents...
      if (a.overdueCount !== b.overdueCount) return b.overdueCount - a.overdueCount;
      // ...then project-linked parties (a deliberate "surface projects" tie-break)...
      const aProj = a.projectCodes.size > 0;
      const bProj = b.projectCodes.size > 0;
      if (aProj !== bProj) return aProj ? -1 : 1;
      // ...then the biggest UNPAID exposure (laekumata / tasumata is the default
      // "what matters"), ties broken by name for determinism.
      if (a.outstanding !== b.outstanding) return b.outstanding - a.outstanding;
      return a.party.localeCompare(b.party);
    });

  const parties: PartyRow[] = ranked.slice(0, topN).map((a) => ({
    party: a.party,
    source: a.source,
    cells: a.cells,
    total: a.total,
    priority: a.priority,
    overdueCount: a.overdueCount,
    projectCodes: [...a.projectCodes].sort(),
    outstanding: a.outstanding,
  }));

  const rest = ranked.slice(topN);
  let others: OthersRow | null = null;
  if (rest.length > 0) {
    const cells = columns.map(() => zeroCell());
    const total = zeroCell();
    for (const a of rest) {
      a.cells.forEach((c, i) => {
        addSum(cells[i].income, c.income);
        addSum(cells[i].cost, c.cost);
      });
      addSum(total.income, a.total.income);
      addSum(total.cost, a.total.cost);
    }
    cells.forEach(finalizeCellNet);
    finalizeCellNet(total);
    others = { count: rest.length, cells, total };
  }

  return { category, parties, others };
}

// ------------------------------------------------------------- KPI + VAT + TSD

/** The KPI strip numbers, all GROSS integer cents (L9) except `marginPct` (whole %). */
export interface WindowSummary {
  /** müük — total income booked in the window. */
  sales: number;
  /** arveldatud — income that is actual or committed (already invoiced). */
  invoiced: number;
  /** arveldamata — income not yet invoiced (sales − invoiced). */
  uninvoiced: number;
  /** prognoos — forecast (uncommitted) income. */
  forecast: number;
  /** tegelik kulu — cost that has actually moved. */
  actualCost: number;
  /** kulu kokku — all cost booked in the window. */
  totalCost: number;
  /** marginaal — sales − totalCost. */
  margin: number;
  /** marginaal % — whole-percent ratio of margin to sales (0 when no sales). */
  marginPct: number;
  /** saldo — the gross closing balance of the window. */
  closingBalance: number;
}

/**
 * Summarise the per-line breakdown into the KPI strip numbers (module 1.1) — the
 * ONE place these headline figures live (master rule: no component sums money).
 * `mode` picks the NET or GROSS side of every P&L figure (the shared display
 * toggle changes only which precomputed field is read — never a recalculation);
 * `closingBalance` is real cash on the account, so it stays GROSS regardless.
 * `marginPct` is a ratio (not money), so it is the only rounded value. Integer
 * cents otherwise (L9); pure and deterministic.
 */
export function summarizeWindow(
  lines: LineRollup[],
  closingBalance: number,
  mode: 'net' | 'gross' = 'gross',
): WindowSummary {
  let sales = 0;
  let invoiced = 0;
  let forecast = 0;
  let actualCost = 0;
  let totalCost = 0;
  for (const line of lines) {
    sales += line.income.total[mode];
    invoiced += line.income.actual[mode] + line.income.committed[mode];
    forecast += line.income.forecast[mode];
    actualCost += line.cost.actual[mode];
    totalCost += line.cost.total[mode];
  }
  const margin = sales - totalCost;
  return {
    sales,
    invoiced,
    uninvoiced: sales - invoiced,
    forecast,
    actualCost,
    totalCost,
    margin,
    marginPct: sales === 0 ? 0 : Math.round((margin / sales) * 100),
    closingBalance,
  };
}

/** The VAT (käibemaks) position of a window — output minus deductible input VAT. */
export interface VatPosition {
  /** Output VAT collected on sales. */
  outputVat: number;
  /** Input VAT paid on deductible costs (direct + operating). */
  inputVat: number;
  /** outputVat − inputVat (what is payable, or a refund when negative). */
  payable: number;
}

/**
 * The KM (VAT) footer row of the treasury matrix — output VAT on sales minus the
 * deductible input VAT on direct + operating costs. The ONE definition of the VAT
 * position (master rule L11 / no component arithmetic). Integer cents (L9).
 * Passing a `certainty` narrows the position to that bucket alone (e.g.
 * `'forecast'` → the plan-only slice the matrix "sh prognoos" sub-line shows);
 * omitted, it reads the totals as before.
 */
export function vatPosition(lines: LineRollup[], certainty?: Certainty): VatPosition {
  const bucket = certainty ?? 'total';
  let outputVat = 0;
  let inputVat = 0;
  for (const line of lines) {
    if (line.category === 'sale') outputVat += line.income[bucket].vat;
    if (line.category === 'direct_cost' || line.category === 'operating_cost') {
      inputVat += line.cost[bucket].vat;
    }
  }
  return { outputVat, inputVat, payable: outputVat - inputVat };
}

export interface FilteredSumOptions {
  /** Today, ISO `YYYY-MM-DD`, in the team time zone (Europe/Tallinn). */
  today: string;
  /** First effective day to include (inclusive); unbounded if omitted. */
  from?: string;
  /** Last effective day to include (inclusive); unbounded if omitted. */
  to?: string;
  /** Reporting basis (defaults to cash) — accrual places by document date. */
  basis?: Basis;
  /** Keep only entries whose RESOLVED certainty ({@link resolveEntry}'s
   *  `effectiveCertainty` — actual once moved) matches; omitted = all. Drives
   *  the TSD row's forecast slice ("sh prognoos") without a component re-sum. */
  certainty?: Certainty;
}

/**
 * The TSD (payroll-tax) footer row — payroll-line entries that came from the tax
 * source, summed over the window with the same carry-forward/window rules as
 * `aggregateByLine` so it reconciles. The ONE definition of the TSD figure
 * (master rule: no component sums money). Integer cents (L9); pure.
 */
export function payrollTax(entries: CashFlowEntry[], options: FilteredSumOptions): MoneySum {
  const { today } = options;
  assertISO(today, 'today');
  if (options.from !== undefined) assertISO(options.from, 'from');
  if (options.to !== undefined) assertISO(options.to, 'to');
  const sum = zeroSum();
  for (const e of entries) {
    if (e.category !== 'payroll' || e.source !== 'tax') continue;
    const r = resolveEntry(e, today, options.basis ?? 'cashBasis');
    if (options.from !== undefined && r.effectiveDate < options.from) continue;
    if (options.to !== undefined && r.effectiveDate > options.to) continue;
    if (options.certainty !== undefined && r.effectiveCertainty !== options.certainty) continue;
    addMoney(sum, e.money);
  }
  return sum;
}

/**
 * The "Maksekokkulepped" informational footer row — every materialized payment-plan
 * installment entry (source `payment_plan`, principal AND interest parts) summed
 * over the window with the same carry-forward/window rules as `aggregateByLine`
 * so it reconciles cell-for-cell. A VISUAL aggregate only: the parts already live
 * inside their category lines (direct costs / financial costs), so this figure
 * must NEVER be added to any total — summing it again would double-count. The ONE
 * definition of the figure (master rule: no component sums money). Integer cents
 * (L9); pure.
 */
export function paymentPlanFlow(entries: CashFlowEntry[], options: FilteredSumOptions): MoneySum {
  const { today } = options;
  assertISO(today, 'today');
  if (options.from !== undefined) assertISO(options.from, 'from');
  if (options.to !== undefined) assertISO(options.to, 'to');
  const sum = zeroSum();
  for (const e of entries) {
    if (e.source !== 'payment_plan') continue;
    const r = resolveEntry(e, today, options.basis ?? 'cashBasis');
    if (options.from !== undefined && r.effectiveDate < options.from) continue;
    if (options.to !== undefined && r.effectiveDate > options.to) continue;
    if (options.certainty !== undefined && r.effectiveCertainty !== options.certainty) continue;
    addMoney(sum, e.money);
  }
  return sum;
}

/**
 * Sum the money of an arbitrary list of entries into a single `MoneySum`. The ONE
 * definition of "add up these documents" (master rule: no component sums money) —
 * the ledger's footer totals call this over the exact rows it displays, after the
 * component has done its (non-monetary) filtering. Integer cents (L9); pure.
 */
export function sumEntries(entries: CashFlowEntry[]): MoneySum {
  const sum = zeroSum();
  for (const e of entries) addMoney(sum, e.money);
  return sum;
}

/**
 * The minimal shape needed to give a document its cash DIRECTION sign: just its
 * `kind`. A structural interface, NOT a change to the math model — every entry
 * (the bare {@link CashFlowEntry}, the API/web entry) satisfies it. A credit note
 * needs no extra field here: its reversal is carried by a NEGATIVE stored amount,
 * so the kind-only sign nets it out (see {@link cashFlowSign}).
 */
export interface CashFlowSignable {
  kind: CashFlowKind;
}

/**
 * The cash DIRECTION sign of a document: +1 for an income (a receivable, cash IN),
 * −1 for a cost (a payable, cash OUT). It depends ONLY on the kind. A CREDIT NOTE
 * is deliberately NOT special-cased: the source data already stores a reversal as a
 * NEGATIVE amount (an income credit note — a refund TO a customer — is a negative
 * receivable; a cost credit note — a refund FROM a supplier — is a negative
 * payable), so applying the direction sign to that already-signed amount yields the
 * correct cash direction (income-credit → cash OUT, cost-credit → cash IN).
 * Re-flipping on `credit_note` would DOUBLE-count and is a bug. The ONE place this
 * sign is decided (master rule) — both the register's per-row amount and its signed
 * footer total read it, so a row and the total can never disagree. Pure and
 * deterministic.
 */
export function cashFlowSign(e: CashFlowSignable): 1 | -1 {
  return e.kind === 'income' ? 1 : -1;
}

/**
 * Sum a list of documents into a SIGNED cash-flow total — each contribution added
 * with its {@link cashFlowSign} (income +, cost −) applied to the stored, already
 * signed amount, so credit notes (stored negative) net out correctly. This is the
 * true "Kokku" of a mixed income/cost register, which an unsigned {@link sumEntries}
 * would overstate. The ONE definition of the signed register total (master rule: no
 * component sums money; L9 — integer cents in, integer cents out). Pure and
 * deterministic.
 */
export function signedSumEntries<
  E extends CashFlowSignable & { money: { net: number; vat: number; gross: number } },
>(entries: readonly E[]): MoneySum {
  const sum = zeroSum();
  for (const e of entries) {
    const s = cashFlowSign(e);
    sum.net += s * e.money.net;
    sum.vat += s * e.money.vat;
    sum.gross += s * e.money.gross;
  }
  return sum;
}

/**
 * Sum a list of documents' money with received prepayments NETTED OUT — the
 * offset-aware twin of {@link sumEntries}. Each entry contributes its {@link
 * effectiveCashFlowMoneyOf} (a final invoice funded by a paid prepayment adds only
 * its unfunded remainder), so a total over an ETT↔ARV pair never counts the same euro
 * twice. The ONE definition of "add up these documents, net of prepayment" (master
 * rule; L9 — integer cents in, integer cents out). Pure and deterministic.
 */
export function sumEffectiveEntries<E extends CashFlowEntry & PrepaymentOffsettable>(
  entries: readonly E[],
): MoneySum {
  const sum = zeroSum();
  for (const e of entries) addMoney(sum, effectiveCashFlowMoneyOf(e));
  return sum;
}

/**
 * The SIGNED cash-flow total with received prepayments NETTED OUT — the offset-aware
 * twin of {@link signedSumEntries}. Each contribution is its {@link
 * effectiveCashFlowMoneyOf} carried by its {@link cashFlowSign} (income +, cost −; a
 * credit note is already stored negative), so the register's "Kokku" over a prepaid
 * lõpparve and its ettemaksuarve never double-counts the funded euro. The ONE
 * definition of the signed, prepayment-netted register total (master rule; L9 —
 * integer cents in, integer cents out). Pure and deterministic.
 */
export function signedEffectiveSumEntries<E extends CashFlowEntry & PrepaymentOffsettable>(
  entries: readonly E[],
): MoneySum {
  const sum = zeroSum();
  for (const e of entries) {
    const s = cashFlowSign(e);
    const m = effectiveCashFlowMoneyOf(e);
    sum.net += s * m.net;
    sum.vat += s * m.vat;
    sum.gross += s * m.gross;
  }
  return sum;
}

/**
 * Outstanding signed by cash DIRECTION (income +, cost −), integer cents — so the
 * register's "Jääk" reads like its "Summa": a still-to-receive income is positive
 * (cash IN), a still-to-pay cost is negative (cash OUT), instead of an unsigned
 * magnitude that reads the same for both. {@link cashFlowSign} × {@link
 * outstandingOf}; a credit note (stored negative) nets out via the same kind-only
 * sign, exactly as {@link signedSumEntries} does for the gross. The ONE place a
 * directional outstanding is decided (master rule; L9 — integer cents). Pure and
 * deterministic.
 */
export function signedOutstandingOf(e: SettleableEntry & CashFlowSignable): number {
  const out = outstandingOf(e);
  // Settled (out === 0) has no direction to show — return a clean 0, never the −0
  // that `cashFlowSign(cost) × 0` would produce (which would render as "−0,00 €").
  return out === 0 ? 0 : cashFlowSign(e) * out;
}

/**
 * Sum a list into a SIGNED outstanding total (income + / cost −), integer cents —
 * the register footer / group "Jääk" as the NET still-open cash effect (tulu −
 * kulu), never an unsigned magnitude sum that would overstate a mixed register.
 * Sums {@link signedOutstandingOf}; the ONE definition of the directional
 * outstanding total (master rule: no component sums money itself; L9 — integer
 * cents in, integer cents out). Pure and deterministic.
 */
export function signedOutstandingSum(
  entries: readonly (SettleableEntry & CashFlowSignable)[],
): number {
  let sum = 0;
  for (const e of entries) sum += signedOutstandingOf(e);
  return sum;
}

/**
 * The cash PAID on an entry, SIGNED by cash direction (income + / cost −),
 * integer cents — so the register's folded "Makstud / Laekunud" figure reads
 * directionally, exactly like the amount and the Jääk: a settled COST shows the
 * cash that went OUT as negative. 0 stays a clean 0 (never the −0 that
 * `cashFlowSign(cost) × 0` would yield → "−0,00 €"). The ONE place a directional
 * paid amount is decided (master rule; L9 — integer cents). Pure.
 */
export function signedPaidAmountOf(e: SettleableEntry & CashFlowSignable): number {
  const paid = paidAmountOf(e);
  return paid === 0 ? 0 : cashFlowSign(e) * paid;
}

// ------------------------------------------------ carried-forward grouping

/** One direction's carried-forward (overdue, unprioritised) rollup for the
 *  calendar's summary group: its money total + the exact document ids behind it. */
export interface CarriedForwardGroup {
  /** Summed money of the grouped documents, integer cents. */
  total: MoneySum;
  /** Ids of the documents folded into this group (drives the drill scope). */
  ids: string[];
}

/** The two carried-forward summary groups the calendar collapses overdue items
 *  into — one per direction (replit.md module 1.1 / L14). */
export interface CarriedForwardPartition {
  income: CarriedForwardGroup;
  cost: CarriedForwardGroup;
}

/**
 * Partition the carried-forward documents (overdue + still pending → rolled to
 * today) into one income and one cost summary group, EXCLUDING any that carry a
 * management priority (those stay distinct prominent chips on the calendar, never
 * folded away — F2). Reuses {@link resolveEntry} for the carry-forward decision
 * (one truth) and sums the money here, never in a component (master rule; L9 —
 * integer cents). Returns the matching document ids so the calendar can open the
 * register scoped to EXACTLY this group. Pure and deterministic.
 */
export function partitionCarriedForwardByDirection<
  E extends CashFlowEntry & { priority?: Priority | null },
>(entries: E[], today: string, basis: Basis = 'cashBasis'): CarriedForwardPartition {
  const income: CarriedForwardGroup = { total: zeroSum(), ids: [] };
  const cost: CarriedForwardGroup = { total: zeroSum(), ids: [] };
  for (const e of entries) {
    if (e.priority) continue;
    // Only the CARRIED slice counts — a partially-paid overdue document folds in
    // with its outstanding jääk only (its paid slice already sits in the past as an
    // actual movement), and the netting by any received prepayment offset rides in
    // via resolvePlacements, so the summary never double-counts (L9). Under
    // `accrualBasis` no slice is ever carried, so both groups stay empty — the
    // calendar's overdue summary disappears structurally, not by a UI hide.
    const carried = resolvePlacements(e, today, basis).filter((p) => p.carriedForward);
    if (carried.length === 0) continue;
    const group = e.kind === 'income' ? income : cost;
    for (const p of carried) addMoney(group.total, p.money);
    group.ids.push(e.id);
  }
  return { income, cost };
}

// ------------------------------------------------------ quarter month-nesting

/** A calendar month inside a quarter, as a `[from, to]` day range. */
export interface MonthSpan {
  /** `YYYY-MM` (matches `periodKey(date, 'month')`). */
  key: string;
  /** First day of the month, ISO. */
  from: string;
  /** Last day of the month, ISO. */
  to: string;
}

const QUARTER_KEY = /^(\d{4})-Q([1-4])$/;

/**
 * Enumerate the three calendar months of a quarter (e.g. `2026-Q2` →
 * Apr/May/Jun), each as an ISO `[from, to]` day range. This is the structural
 * primitive behind the table's quarter→month drill-down (module 1.1): the month
 * ranges feed straight into `aggregatePeriods`/`aggregateLineParties` as columns,
 * so a quarter's nested months RECONCILE cent-for-cent with the quarter itself
 * (master rule: one truth per number). Language-agnostic — the Estonian
 * "Apr–Juuni" label is the UI layer's job. Pure and deterministic.
 */
export function quarterMonths(quarterKey: string): MonthSpan[] {
  const m = QUARTER_KEY.exec(quarterKey);
  if (!m) {
    throw new RangeError(`quarterMonths: expected a quarter key (YYYY-Qn), received ${quarterKey}`);
  }
  const year = Number(m[1]);
  const quarter = Number(m[2]);
  const startMonth = (quarter - 1) * 3 + 1; // 1, 4, 7, 10
  const out: MonthSpan[] = [];
  for (let i = 0; i < 3; i++) {
    const month = startMonth + i;
    const mm = String(month).padStart(2, '0');
    // Day 0 of the next month is the last day of this month (UTC, DST-safe).
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    out.push({
      key: `${year}-${mm}`,
      from: `${year}-${mm}-01`,
      to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
    });
  }
  return out;
}

// ------------------------------------------------------- simulation overlay

/**
 * A local "what-if" change to one entry, applied on top of the base stream
 * WITHOUT persisting anything (the canvas "Simuleeri muudatus" overlay). Three
 * moves mirror the planning surface:
 *  - `reschedule` — move the expected date (a forecast/committed entry slips).
 *  - `amount`     — replace the entry's `Money` (re-estimate a figure).
 *  - `split`      — break one entry into ≥2 installments that MUST sum back to the
 *                   original `Money` (so the simulation conserves the total).
 */
export type SimulationDelta =
  | { type: 'reschedule'; id: string; expectedDate: string }
  | { type: 'amount'; id: string; money: Money }
  | {
      type: 'split';
      id: string;
      parts: { idSuffix: string; expectedDate: string; money: Money }[];
    };

/** The recomputed view after a set of simulation deltas — all via finance-core. */
export interface SimulationResult {
  /** The simulated entry stream (base with deltas applied). */
  entries: CashFlowEntry[];
  /** Per-day rollup of the simulated stream. */
  days: DayRollup[];
  /** Period columns of the simulated stream at the requested granularity. */
  periods: PeriodRollup[];
  /** The window grand-total of the simulated stream. */
  window: WindowTotal;
}

function sameMoneyTotal(a: MoneySum, m: Money): boolean {
  return a.net === m.net && a.vat === m.vat && a.gross === m.gross;
}

/**
 * Apply local simulation deltas to a base entry stream, returning a NEW stream
 * (the input is never mutated). The ONE place a what-if change is expressed
 * (master rule: no component invents its own overlay logic); the result is fed
 * back through the same rollup so totals stay honest. A `split` must conserve the
 * original `Money` cent-for-cent. Pure and deterministic; integer cents only (L9).
 */
export function applySimulation(
  entries: CashFlowEntry[],
  deltas: SimulationDelta[],
): CashFlowEntry[] {
  const work = entries.map((e) => ({ ...e }));
  for (const delta of deltas) {
    const idx = work.findIndex((e) => e.id === delta.id);
    if (idx === -1) {
      throw new RangeError(`applySimulation: no entry with id ${delta.id}`);
    }
    const target = work[idx];
    if (delta.type === 'reschedule') {
      assertISO(delta.expectedDate, 'reschedule.expectedDate');
      work[idx] = { ...target, expectedDate: delta.expectedDate };
    } else if (delta.type === 'amount') {
      work[idx] = { ...target, money: delta.money };
    } else {
      if (delta.parts.length < 2) {
        throw new RangeError('applySimulation: a split needs at least 2 parts');
      }
      const partsSum = zeroSum();
      for (const part of delta.parts) {
        assertISO(part.expectedDate, 'split.part.expectedDate');
        addMoney(partsSum, part.money);
      }
      if (!sameMoneyTotal(partsSum, target.money)) {
        throw new RangeError('applySimulation: split parts must sum to the original amount');
      }
      const partIds = delta.parts.map((part) => `${target.id}-${part.idSuffix}`);
      if (new Set(partIds).size !== partIds.length) {
        throw new RangeError('applySimulation: split part idSuffix values must be unique');
      }
      const existingIds = new Set(work.map((e) => e.id));
      existingIds.delete(target.id);
      for (const partId of partIds) {
        if (existingIds.has(partId)) {
          throw new RangeError(`applySimulation: split would create id ${partId}, which already exists`);
        }
      }
      const newParts: CashFlowEntry[] = delta.parts.map((part) => ({
        ...target,
        id: `${target.id}-${part.idSuffix}`,
        expectedDate: part.expectedDate,
        actualDate: null,
        money: part.money,
      }));
      work.splice(idx, 1, ...newParts);
    }
  }
  return work;
}

/**
 * Run a simulation end-to-end: apply the deltas, then roll the result up exactly
 * like the live view (`rollupDays` → `aggregatePeriods` → `sumPeriods`). The
 * single entry point a what-if surface calls so its numbers come only from
 * finance-core (master rule). Pure; integer cents (L9).
 */
export function simulate(
  entries: CashFlowEntry[],
  deltas: SimulationDelta[],
  options: RollupOptions,
  gran: Granularity,
): SimulationResult {
  const simulated = applySimulation(entries, deltas);
  const days = rollupDays(simulated, options);
  const periods = aggregatePeriods(days, gran);
  const window = sumPeriods(periods);
  return { entries: simulated, days, periods, window };
}
